import type { RequestContext } from "../application/context/request-context.js";
import type { AuthorizationService } from "../permissions/authorization.js";
import { PermissionCode, RoleCode } from "../domain/identity/access-types.js";
import { authenticateWithCredentials, passwordMatches, type CredentialAuthentication } from "../domain/identity/credential-authentication.js";
import { hashPassword, isHashedPassword } from "../domain/identity/password-hash.js";
import { passwordPolicyViolations, type SecuritySettings } from "../domain/settings/system-settings.js";
import {
  buildPeopleDirectory,
  findPeopleMigrationPendencies,
  findRelinkCandidates,
  type RelinkResolution,
  type PeopleMigrationPendency,
  type ProfessionalDirectoryView
} from "../domain/identity/people-directory.js";
import type { AuditRecorder } from "../audit/audit-recorder.js";
import { AuditAction } from "../audit/audit-log.js";
import { AppError, ErrorCode } from "../shared/errors/app-error.js";
import type {
  AdminRepository,
  Area,
  WorkOrder,
  SubWorkOrder,
  ServiceFront,
  Signatory,
  EnrichedProfessionalSubOsAccess,
  ProfessionalSubOsAccess
} from "./admin-domain.js";
import type { IdentityRepository } from "../infrastructure/database/memory/in-memory-identity-repositories.js";
import type { Client } from "../domain/client/client.js";
import { ClientStatus } from "../domain/client/client.js";
import type { User } from "../domain/identity/user.js";
import { UserStatus } from "../domain/identity/user.js";
import { validateProfileImage } from "../domain/identity/user-avatar.js";
import type { Professional } from "../domain/identity/professional.js";
import { ProfessionalStatus, ProfessionalType } from "../domain/identity/professional.js";
import { isEligibleToBeNamedPlh } from "../domain/identity/plh-eligibility.js";
import { isPlhQualifiedProfessional } from "../domain/identity/plh-eligibility.js";
import { PLH_SPECIFIC_PERMISSIONS } from "../permissions/built-in-role-policy.js";
import { workOrdersForProjectContext } from "../domain/project/project-work-order-cascade.js";
import type { UserClientAccess, Role } from "../domain/identity/access-control.js";
import { UserClientAccessStatus } from "../domain/identity/access-control.js";
import type { ClientId, AreaId, WorkOrderId, ServiceFrontId, SignatoryId, UserId, ProfessionalId, RoleId, UserClientAccessId, CompanyId } from "../shared/kernel/identifiers.js";
import { notificationService } from "../application/services/notification-service.js";
import type { Company } from "../domain/identity/company.js";
import { CompanyStatus, formatCnpj, normalizeCnpj, isValidCnpj, isValidCnpjFormat } from "../domain/identity/company.js";
import type { CompanyRepository } from "../infrastructure/database/memory/in-memory-company-repository.js";

export class AdminService {
  /**
   * CONFIGURAÇÕES → SEGURANÇA → POLÍTICA DE SENHA. Sem ela ligada vale o
   * mínimo de sempre (8 caracteres na redefinição). Com ela, toda senha
   * DIGITADA — criação de conta, troca e redefinição — é conferida aqui, no
   * serviço, e nunca só na tela.
   */
  private passwordPolicy: (() => Promise<SecuritySettings>) | null = null;

  attachPasswordPolicy(provider: () => Promise<SecuritySettings>): void {
    this.passwordPolicy = provider;
  }

  private async assertPasswordPolicy(password: string): Promise<void> {
    if (!this.passwordPolicy) return;
    const policy = await this.passwordPolicy();
    const problems = passwordPolicyViolations(policy, password);
    if (problems.length > 0) {
      throw new AppError(ErrorCode.Validation, "Password policy", { safeMessage: `A senha precisa ${problems.join(", ")}.` });
    }
  }

  constructor(
    private readonly adminRepo: AdminRepository,
    private readonly identityRepo: IdentityRepository,
    private readonly companyRepo: CompanyRepository,
    private readonly auth: AuthorizationService,
    private readonly audit?: AuditRecorder,
    private readonly projectChecker?: { getProject: (id: string) => Promise<{ id: string; clientId?: string } | null> },
    /** Returns true when the given userId has preserved history elsewhere (e.g. authored an Engineering Minuta analysis/validation/approval) that must survive User deletion. See `createUserHistoryChecker` in browser-platform.ts. */
    private readonly userHistoryChecker?: (userId: string) => Promise<boolean>
  ) {}

  private requireAny(context: RequestContext, permissions: readonly PermissionCode[]): void {
    if (permissions.length === 0) return;
    const first = permissions[0]!;
    if (this.auth && typeof this.auth.requireAny === "function") {
      this.auth.requireAny(context, permissions);
    } else if (this.auth && typeof this.auth.require === "function") {
      if (typeof this.auth.canAny === "function") {
        if (!this.auth.canAny(context, permissions)) {
          this.auth.require(context, first);
        }
      } else if (typeof this.auth.can === "function") {
        const hasAny = permissions.some((p) => this.auth.can(context, p));
        if (!hasAny) {
          this.auth.require(context, first);
        }
      } else {
        this.auth.require(context, first);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // CLIENTS
  // ---------------------------------------------------------------------------
  public async listClients(context: RequestContext): Promise<Client[]> {
    this.auth.require(context, PermissionCode.ClientAccess);
    return this.identityRepo.listClients();
  }

  /**
   * OS CLIENTES QUE ESTE USUÁRIO ENXERGA.
   *
   * ────────────────────────────────────────────────────────────────────
   * `UserClientAccess` é uma RESTRIÇÃO, não uma concessão.
   * ────────────────────────────────────────────────────────────────────
   *
   * Antes, quem não tivesse `AdministrationManage` só via Clientes para os
   * quais existisse um registro de acesso. Como criar um Cliente NÃO cria
   * esses registros, o efeito prático era: o Administrador cadastrava
   * Cliente, Projeto, OS, Sub-OS e Equipamentos, e ao entrar como Inspetor
   * ou PLH o sistema respondia "Nenhum cliente ativo autorizado para o seu
   * usuário" — como se os cadastros tivessem sumido. Não tinham: a lista
   * chegava vazia porque a ausência de restrição estava sendo lida como
   * ausência de permissão.
   *
   * Cliente, Projeto, OS, Sub-OS e Equipamento são registros CORPORATIVOS.
   * O usuário não é dono deles; tem permissões sobre eles. A regra passa a
   * distinguir os dois casos:
   *
   *   sem NENHUM registro de acesso  → não há restrição configurada,
   *                                    enxerga todos os Clientes ativos;
   *   com registros de acesso        → há restrição explícita,
   *                                    enxerga apenas os concedidos.
   *
   * Isto NÃO afrouxa o RBAC: `ClientAccess` continua exigida acima, e criar,
   * editar, excluir, aprovar e assinar seguem com os seus próprios gates.
   * O que muda é apenas VER O DADO NECESSÁRIO AO TRABALHO — que nunca foi a
   * mesma coisa que poder alterá-lo.
   */
  public async listAuthorizedClients(context: RequestContext): Promise<Client[]> {
    this.auth.require(context, PermissionCode.ClientAccess);
    const allClients = await this.identityRepo.listClients();
    const activeClients = allClients.filter(c => c.status === ClientStatus.Active && c.deletedAt === null);

    if (context.permissions.has(PermissionCode.AdministrationManage)) {
      return activeClients;
    }

    const allowed = await this.restrictedClientIdsOf(context.userId as any);
    if (allowed === null) return activeClients;
    return activeClients.filter(c => allowed.has(c.id));
  }

  /**
   * Os Clientes a que este usuário está RESTRITO, ou `null` quando não há
   * restrição configurada para ele.
   *
   * `null` e conjunto vazio são coisas diferentes, e confundi-los foi o
   * defeito: `null` = "ninguém limitou este usuário"; conjunto vazio =
   * "existem registros de acesso, mas todos inativos/removidos" — aí a
   * restrição existe e nega tudo, que é o comportamento correto para um
   * acesso revogado.
   */
  private async restrictedClientIdsOf(userId: string | null | undefined): Promise<Set<string> | null> {
    if (!userId) return null;
    const accesses = await this.identityRepo.listAccesses(userId as any);
    if (!accesses || accesses.length === 0) return null;
    return new Set(
      accesses
        .filter(a => a.status === UserClientAccessStatus.Active && a.deletedAt === null)
        .map(a => String(a.clientId))
    );
  }

  public async assertUserHasAccessToClient(context: RequestContext, clientId: string): Promise<Client> {
    if (!clientId || !clientId.trim()) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O CLIENTE.");
    }
    const client = await this.identityRepo.getClient(clientId as ClientId);
    if (!client || client.deletedAt !== null) {
      throw new AppError(ErrorCode.NotFound, "Cliente não encontrado.", {
        safeMessage: "Cliente não encontrado."
      });
    }
    if (client.status !== ClientStatus.Active) {
      throw new AppError(ErrorCode.Validation, "O cliente selecionado está inativo.");
    }

    if (context.permissions.has(PermissionCode.AdministrationManage)) {
      return client;
    }

    // Mesma regra de `listAuthorizedClients`: sem NENHUM registro de acesso
    // não há restrição configurada para este usuário, e o Cliente corporativo
    // é acessível. Com registros, vale a restrição. Se este gate usasse um
    // critério diferente do da listagem, o Cliente apareceria na lista e a
    // ação seguinte seria negada — a pior das duas respostas.
    const allowed = await this.restrictedClientIdsOf(context.userId as any);
    const hasActiveAccess = allowed === null || allowed.has(String(clientId));

    if (!hasActiveAccess) {
      throw new AppError(ErrorCode.Authorization, "Acesso negado para o cliente selecionado.", {
        safeMessage: "Você não possui autorização para este cliente."
      });
    }

    return client;
  }

  public async getClient(context: RequestContext, id: string): Promise<Client | null> {
    this.auth.require(context, PermissionCode.ClientAccess);
    return this.identityRepo.getClient(id as ClientId);
  }

  public async createClient(
    context: RequestContext,
    data: { code: string; name: string; legalName: string; document: string }
  ): Promise<Client> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    
    const existing = await this.identityRepo.listClients();
    const normalizedCode = data.code.trim().toUpperCase();
    if (existing.some(c => c.code.trim().toUpperCase() === normalizedCode)) {
      throw new AppError(ErrorCode.Conflict, `Código de cliente '${data.code}' já existe.`);
    }

    if (!data.document || !isValidCnpj(data.document)) {
      throw new AppError(ErrorCode.Validation, "O CNPJ da empresa é obrigatório e deve ser válido (com 14 dígitos e dígitos verificadores corretos).");
    }

    const formattedDocument = formatCnpj(data.document);

    const now = new Date().toISOString();
    const id = `client-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const client: Client = {
      id: id as ClientId,
      code: normalizedCode,
      name: data.name.trim(),
      legalName: data.legalName.trim(),
      document: formattedDocument,
      status: ClientStatus.Active,
      version: 1,
      createdAt: now as any,
      updatedAt: now as any,
      deletedAt: null,
      deletedBy: null,
      deletionReason: null,
      createdBy: context.userId,
      updatedBy: context.userId
    };

    await this.identityRepo.saveClient(client);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "Client",
        entityId: client.id,
        previousState: null,
        newState: { code: client.code, name: client.name },
        metadata: { action: "CLIENT_CREATED" }
      }).catch(() => {});
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'CLIENT', id: client.id } }));
    }

    return client;
  }

  /**
   * Transferência de pendentes na troca de PLH. Porta opcional: o
   * AdminService não conhece o ciclo de revisão, e o serviço de revisão não
   * conhece Clientes. A composição (browser/backend-platform) liga os dois.
   */
  private plhReassignment: {
    listReassignablePendingForClient(context: RequestContext, clientId: string, currentPlhProfessionalId?: string | null): Promise<readonly { documentId: string }[]>;
    reassignPendingToPlh(context: RequestContext, clientId: string, newPlhProfessionalId: string, newPlhName: string, currentPlhProfessionalId?: string | null): Promise<readonly { documentId: string }[]>;
  } | null = null;

  public attachPlhReassignment(port: NonNullable<AdminService["plhReassignment"]>): void {
    this.plhReassignment = port;
  }

  /**
   * O que o Administrador precisa ver ANTES de confirmar a troca: quem é o
   * PLH atual, quem passa a ser e quantos documentos pendentes estão em jogo.
   */
  public async previewClientPlhReplacement(
    context: RequestContext, clientId: string, newPlhProfessionalId: string
  ): Promise<{ currentPlh: Professional | null; nextPlh: Professional | null; pendingCount: number }> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const client = await this.identityRepo.getClient(clientId as ClientId);
    if (!client) throw new AppError(ErrorCode.NotFound, "Client not found", { safeMessage: "Cliente não encontrado." });

    const currentId = client.responsiblePlhProfessionalId ? String(client.responsiblePlhProfessionalId) : null;
    const currentPlh = currentId ? await this.identityRepo.getProfessional(currentId as ProfessionalId).catch(() => null) : null;
    const nextPlh = await this.identityRepo.getProfessional(newPlhProfessionalId as ProfessionalId).catch(() => null);
    const pending = this.plhReassignment && currentId
      ? await this.plhReassignment.listReassignablePendingForClient(context, clientId, currentId).catch(() => [])
      : [];
    return { currentPlh, nextPlh, pendingCount: pending.length };
  }

  /**
   * SUBSTITUIR O PLH RESPONSÁVEL DO CLIENTE.
   *
   * Depois da troca, TODO documento novo do Cliente resolve o novo PLH — isso
   * é consequência automática de `Client.responsiblePlhProfessionalId` ser a
   * fonte única (seção 24), não de copiar nada.
   *
   * Os pendentes do passado são decisão do Administrador: `reassignPending`
   * transfere os que ainda dependem de atuação do PLH; `false` deixa-os com
   * o responsável anterior. Documentos já aprovados, finalizados ou assinados
   * NUNCA são tocados — ver `listReassignablePendingForClient`.
   */
  public async replaceClientPlh(
    context: RequestContext,
    clientId: string,
    newPlhProfessionalId: string,
    options: { reassignPending: boolean }
  ): Promise<{ client: Client; reassignedCount: number }> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const client = await this.identityRepo.getClient(clientId as ClientId);
    if (!client) throw new AppError(ErrorCode.NotFound, "Client not found", { safeMessage: "Cliente não encontrado." });

    const previousPlhId = client.responsiblePlhProfessionalId ? String(client.responsiblePlhProfessionalId) : null;
    if (previousPlhId === String(newPlhProfessionalId)) {
      return { client, reassignedCount: 0 };
    }

    // A troca passa pelo caminho oficial: mesma validação, mesma habilitação
    // e mesma auditoria de `updateClient`. Nada de um segundo caminho de
    // gravação do PLH.
    const updated = await this.updateClient(context, { ...client, responsiblePlhProfessionalId: newPlhProfessionalId } as Client);
    const nextPlh = await this.identityRepo.getProfessional(newPlhProfessionalId as ProfessionalId).catch(() => null);

    let reassignedCount = 0;
    if (options.reassignPending && this.plhReassignment && nextPlh) {
      // A transferência vem DEPOIS da troca gravada. Se ela falhar, o Cliente
      // já está com o PLH novo e os pendentes seguem com o anterior — estado
      // consistente e visível, nunca um meio-termo silencioso.
      const moved = await this.plhReassignment.reassignPendingToPlh(
        context, clientId, String(nextPlh.id), nextPlh.name, previousPlhId
      );
      reassignedCount = moved.length;
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.ClientPlhReplaced,
        entityType: "Client",
        entityId: String(clientId),
        previousState: { responsiblePlhProfessionalId: previousPlhId },
        newState: { responsiblePlhProfessionalId: String(newPlhProfessionalId) },
        metadata: {
          clientId: String(clientId), oldPlhProfessionalId: previousPlhId,
          newPlhProfessionalId: String(newPlhProfessionalId),
          pendingDocumentsOption: options.reassignPending ? "REASSIGN" : "KEEP_WITH_PREVIOUS",
          reassignedCount, replacedBy: context.userId ?? null, replacedAt: new Date().toISOString()
        }
      }).catch(() => {});
    }
    return { client: updated, reassignedCount };
  }

  public async updateClient(context: RequestContext, client: Client): Promise<Client> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const existing = await this.identityRepo.getClient(client.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Cliente não encontrado.");
    }

    if (client.document && !isValidCnpj(client.document)) {
      throw new AppError(ErrorCode.Validation, "O CNPJ da empresa é inválido (deve conter 14 dígitos e dígitos verificadores corretos).");
    }

    const previousPlhId = existing.responsiblePlhProfessionalId ?? null;
    const nextPlhId = client.responsiblePlhProfessionalId ?? null;
    if (nextPlhId !== previousPlhId) {
      await this.assertClientPlhValid(nextPlhId, String(client.id));
    }

    const updated: Client = {
      ...client,
      document: client.document ? formatCnpj(client.document) : client.document,
      responsiblePlhProfessionalId: nextPlhId,
      version: (existing.version || 1) + 1,
      updatedAt: new Date().toISOString() as any,
      updatedBy: context.userId
    };

    await this.identityRepo.saveClient(updated);

    // Nada é gravado no Professional: a nomeação JÁ é a habilitação, porque o
    // gate lê `Client.responsiblePlhProfessionalId`. Escrever `plhClientId`
    // aqui recriaria a segunda fonte que esta mudança eliminou.

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Client",
        entityId: client.id,
        previousState: { name: existing.name, status: existing.status, responsiblePlhProfessionalId: previousPlhId },
        newState: { name: updated.name, status: updated.status, responsiblePlhProfessionalId: nextPlhId },
        metadata: { action: nextPlhId !== previousPlhId ? "CLIENT_RESPONSIBLE_PLH_UPDATED" : "CLIENT_UPDATED", previousPlhId, newPlhId: nextPlhId }
      }).catch(() => {});
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'CLIENT', id: client.id } }));
    }

    return updated;
  }

  /**
   * PLH RESPONSÁVEL TÉCNICO do Cliente: um único Professional, sempre pelo
   * gate oficial `isPlhQualifiedProfessional` (ativo, ENGINEER, habilitado
   * como PLH para ESTE Cliente e com assinatura cadastrada). `null` limpa o
   * vínculo e é sempre aceito.
   */
  /**
   * O que se exige para NOMEAR alguém PLH responsável de um Cliente.
   *
   * Não se pode aplicar aqui o gate completo `isPlhQualifiedProfessional`:
   * ele exige `plhClientId === clientId`, isto é, exige que a pessoa JÁ seja
   * PLH deste Cliente. Usá-lo na nomeação é circular — ninguém podia ser o
   * primeiro, e o campo ficava permanentemente NÃO DEFINIDO.
   *
   * A nomeação exige: Profissional existente, ATIVO, ENGENHEIRO e com
   * assinatura cadastrada — `isEligibleToBeNamedPlh`. O que NÃO se exige
   * mais é que ele já estivesse habilitado para este Cliente (condição
   * circular) nem que estivesse livre de outros Clientes: desde 2026-09-24 a
   * habilitação vive no Cliente, e um Engenheiro pode responder por vários.
   */
  private async assertClientPlhValid(professionalId: string | null, clientId: string): Promise<void> {
    if (!professionalId) return;
    const professional = await this.identityRepo.getProfessional(professionalId as ProfessionalId).catch(() => null);
    if (!professional || professional.deletedAt) {
      throw new AppError(ErrorCode.Validation, "PLH não encontrado.", { safeMessage: "O PLH responsável selecionado não foi encontrado no cadastro de Profissionais." });
    }
    const eligible = isEligibleToBeNamedPlh(professional);
    if (!eligible.allowed) {
      const detail = /INATIVO/.test(eligible.reason || "") ? `${professional.name} está INATIVO no cadastro.`
        : /ENGINEER/.test(eligible.reason || "") ? `${professional.name} não está cadastrado como Engenheiro (ENGINEER).`
        : `${professional.name} não possui imagem de assinatura cadastrada. Cadastre a assinatura em Pessoas e Acessos antes de nomeá-lo.`;
      throw new AppError(ErrorCode.Validation, "PLH not eligible", { safeMessage: `${detail} Não pode ser o PLH responsável deste Cliente.` });
    }

    // O mesmo Engenheiro pode ser PLH de quantos Clientes forem necessários:
    // a habilitação vive no CLIENTE, e nenhum Cliente interfere no outro.
  }

  /**
   * Candidatos a PLH RESPONSÁVEL de um Cliente — só quem passa pelo gate
   * oficial para ESTE Cliente. Lê todos os Profissionais (um PLH pode estar
   * lotado em outra empresa e habilitado para este Cliente via plhClientId).
   */
  /**
   * @deprecated Mantido para quem precisa dos JÁ HABILITADOS. Para o seletor
   * de PLH do Cliente use `listEngineerProfessionals`.
   */
  public async listPlhCandidatesForClient(context: RequestContext, _clientId: string): Promise<Professional[]> {
    this.auth.require(context, PermissionCode.IdentityRead);
    const all = await this.identityRepo.listProfessionals();
    // Candidatos são os ELEGÍVEIS a serem nomeados. Quem É o responsável é
    // decisão do Cliente — perguntar isso aqui recriaria a segunda fonte.
    return all.filter(p => isEligibleToBeNamedPlh(p).allowed);
  }

  /**
   * ENGENHEIROS PARA O SELETOR DE PLH DO CLIENTE.
   *
   * Lista TODOS os Professionals classificados como Engenheiro, venham ou não
   * com conta de acesso, assinatura, Projeto ou vínculo com outro Cliente —
   * um mesmo engenheiro pode ser PLH de vários Clientes.
   *
   * O seletor NÃO pode usar `isPlhQualifiedProfessional`: esse gate exige
   * `plhClientId === clientId`, isto é, exige que a pessoa JÁ seja PLH
   * daquele Cliente. Usá-lo para montar a lista criava um círculo vicioso —
   * ninguém podia ser escolhido porque ninguém ainda estava escolhido, e o
   * campo aparecia vazio.
   *
   * O gate continua valendo onde é devido: na hora de aprovar e assinar.
   * Aqui só se escolhe o responsável; o que falta para ele atuar vem em
   * `missingForApproval`, para a tela poder avisar sem esconder ninguém.
   */
  public async listEngineerProfessionals(
    context: RequestContext,
    clientId?: string | null
  ): Promise<Array<Professional & { missingForApproval: string[] }>> {
    this.auth.require(context, PermissionCode.IdentityRead);
    const all = await this.identityRepo.listProfessionals();
    return all
      .filter(p => !p.deletedAt && p.professionalType === ProfessionalType.Engineer)
      .map(p => {
        const missing: string[] = [];
        if (p.status !== ProfessionalStatus.Active) missing.push("Profissional inativo");
        if (!p.signatureImageId) missing.push("sem assinatura cadastrada");
        if (clientId && p.plhClientId !== clientId) missing.push("habilitação de PLH para este Cliente ainda não registrada");
        return { ...p, missingForApproval: missing };
      })
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }));
  }

  /** PLH responsável do Cliente já resolvido (ou null) — para exibir no cadastro. */
  public async getClientResponsiblePlh(context: RequestContext, clientId: string): Promise<Professional | null> {
    this.auth.require(context, PermissionCode.ClientAccess);
    const client = await this.identityRepo.getClient(clientId as ClientId);
    const id = client?.responsiblePlhProfessionalId;
    if (!id) return null;
    return this.identityRepo.getProfessional(id as ProfessionalId).catch(() => null);
  }

  public async inactivateClient(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    await this.identityRepo.inactivateClient(id as ClientId);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Inactivate,
        entityType: "Client",
        entityId: id,
        previousState: null,
        newState: { status: "INACTIVE" },
        metadata: { action: "CLIENT_INACTIVATED" }
      }).catch(() => {});
    }
  }

  public async deleteClient(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    
    const wos = await this.adminRepo.getWorkOrders(id as ClientId);
    if (wos.length > 0) {
      throw new AppError(ErrorCode.Conflict, "Não é possível excluir o cliente pois existem Ordens de Serviço (OS) vinculadas a ele. Utilize a opção Inativar.");
    }
    const profs = await this.identityRepo.listProfessionals(id as ClientId);
    if (profs.length > 0) {
       throw new AppError(ErrorCode.Conflict, "Não é possível excluir o cliente pois existem Profissionais vinculados a ele. Utilize a opção Inativar.");
    }

    await this.identityRepo.deleteClient(id as ClientId);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "Client",
        entityId: id,
        previousState: null,
        newState: null,
        metadata: { action: "CLIENT_DELETED" }
      }).catch(() => {});
    }
  }

  // ---------------------------------------------------------------------------
  // COMPANIES
  // ---------------------------------------------------------------------------
  public async listCompanies(context: RequestContext): Promise<Company[]> {
    this.auth.require(context, PermissionCode.AdministrationRead);
    return this.companyRepo.listCompanies(context.activeClientId);
  }

  public async createCompany(
    context: RequestContext,
    data: { legalName: string; tradeName: string; cnpj: string }
  ): Promise<Company> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    
    if (!data.cnpj || !isValidCnpj(data.cnpj)) {
      throw new AppError(ErrorCode.Validation, "O CNPJ da empresa é obrigatório e deve ser válido (14 dígitos com verificadores corretos).");
    }

    const normalizedCnpj = normalizeCnpj(data.cnpj);
    const existing = await this.companyRepo.getCompanyByNormalizedCnpj(context.activeClientId, normalizedCnpj);
    if (existing) {
      throw new AppError(ErrorCode.Conflict, `O CNPJ ${formatCnpj(normalizedCnpj)} já está cadastrado.`);
    }

    const now = new Date().toISOString();
    const id = `cmp-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const company: Company = {
      id: id as CompanyId,
      clientId: context.activeClientId,
      legalName: data.legalName.trim(),
      tradeName: data.tradeName.trim(),
      cnpj: formatCnpj(normalizedCnpj),
      normalizedCnpj,
      status: CompanyStatus.Active,
      version: 1,
      createdAt: now as any,
      updatedAt: now as any,
      deletedAt: null,
      deletedBy: null,
      deletionReason: null
    };

    await this.companyRepo.saveCompany(company);
    return company;
  }

  public async updateCompany(context: RequestContext, company: Company): Promise<Company> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    if (company.clientId !== context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, "Acesso negado para outro cliente.");
    }
    if (company.cnpj && !isValidCnpj(company.cnpj)) {
      throw new AppError(ErrorCode.Validation, "O CNPJ da empresa é inválido.");
    }
    const normalizedCnpj = company.cnpj ? normalizeCnpj(company.cnpj) : company.normalizedCnpj;
    const updated: Company = {
      ...company,
      cnpj: company.cnpj ? formatCnpj(normalizedCnpj) : company.cnpj,
      normalizedCnpj,
      updatedAt: new Date().toISOString() as any,
      version: (company.version || 1) + 1
    };
    await this.companyRepo.saveCompany(updated);
    return updated;
  }

  public async inactivateCompany(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const company = await this.companyRepo.getCompany(id as CompanyId);
    if (company && company.clientId === context.activeClientId) {
      await this.companyRepo.saveCompany({
        ...company,
        status: CompanyStatus.Inactive,
        updatedAt: new Date().toISOString() as any,
        version: (company.version || 1) + 1
      });
    }
  }

  // ---------------------------------------------------------------------------
  // AREAS
  // ---------------------------------------------------------------------------
  public async listAreas(context: RequestContext, clientId?: string): Promise<Area[]> {
    this.auth.require(context, PermissionCode.AdministrationRead);
    if (clientId) {
      return this.adminRepo.getAreas(clientId as ClientId);
    }
    if (context.activeClientId) {
      return this.adminRepo.getAreas(context.activeClientId);
    }
    return this.adminRepo.getAreas();
  }

  public async createArea(
    context: RequestContext,
    data: { clientId?: string; name: string; description?: string | null }
  ): Promise<Area> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const targetClientId = data.clientId || context.activeClientId;
    if (!targetClientId || !targetClientId.trim()) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O CLIENTE.");
    }

    await this.assertUserHasAccessToClient(context, targetClientId);

    const existing = await this.adminRepo.getAreas(targetClientId as ClientId);
    const normalizedName = data.name.trim().toUpperCase();
    if (existing.some(a => a.name.trim().toUpperCase() === normalizedName)) {
      throw new AppError(ErrorCode.Conflict, `Área '${data.name}' já cadastrada para este cliente.`);
    }

    const now = new Date().toISOString();
    const id = `area-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const area: Area = {
      id: id as AreaId,
      clientId: targetClientId as ClientId,
      name: data.name.trim(),
      description: data.description ? data.description.trim() : null,
      status: 'ACTIVE',
      createdAt: now,
      updatedAt: now
    };

    await this.adminRepo.saveArea(area);
    return area;
  }

  public async updateArea(context: RequestContext, area: Area): Promise<Area> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const updated: Area = {
      ...area,
      updatedAt: new Date().toISOString()
    };
    await this.adminRepo.saveArea(updated);
    return updated;
  }

  public async inactivateArea(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    await this.adminRepo.inactivateArea(context.activeClientId, id as AreaId);
  }

  public async deleteArea(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    await this.adminRepo.deleteArea(id as AreaId);
  }

  // ---------------------------------------------------------------------------
  // WORK ORDERS (OS)
  // ---------------------------------------------------------------------------
  /**
   * OS/Sub-OS lookup for filling a document (Bloco 19). Gated by
   * `sub_os.search` instead of `administration.read`: an Inspector fills the
   * OS of their own ME report without being granted administrative reach.
   */
  /**
   * As OS que um documento pode escolher no contexto Cliente (+ Projeto).
   *
   * O filtro por Projeto usa a REGRA OFICIAL (`workOrdersForProjectContext`),
   * e não `wo.projectId === projectId`. O filtro antigo produzia
   * "Nenhuma OS cadastrada para este contexto" com OS existentes, porque
   * `WorkOrder.projectId` é campo legado e `null` é o estado normal de uma OS
   * criada só com Cliente — que é o que a seção 23 do manual determina.
   */
  public async listWorkOrdersForDocument(context: RequestContext, clientId?: string, projectId?: string): Promise<WorkOrder[]> {
    this.auth.require(context, PermissionCode.SubOsSearch);
    const list = clientId
      ? await this.adminRepo.getWorkOrders(clientId as ClientId)
      : await this.adminRepo.getWorkOrders();
    if (!projectId) return list;

    const links = this.adminRepo.getWorkOrderProjectLinks
      ? await this.adminRepo.getWorkOrderProjectLinks().catch(() => [])
      : [];
    return workOrdersForProjectContext(list, projectId, links as never);
  }

  /**
   * As Sub-OS que um documento pode escolher.
   *
   * `workOrderId` é OPCIONAL: telas operacionais (Checklist, RDO) precisam do
   * conjunto para montar a cascata antes de a OS estar escolhida. Elas usavam
   * `listSubWorkOrders`, que exige `administration.read` — permissão que o
   * Inspetor e o Técnico não têm, e nem deveriam ter: ler a Sub-OS do próprio
   * trabalho não é administrar o cadastro de Sub-OS. O resultado era
   * "Você não possui permissão de leitura para Sub-OS" no meio da inspeção.
   *
   * O gate aqui é `sub_os.search`, que é a leitura operacional.
   */
  public async listSubWorkOrdersForDocument(context: RequestContext, workOrderId?: string): Promise<SubWorkOrder[]> {
    this.auth.require(context, PermissionCode.SubOsSearch);
    return workOrderId
      ? this.adminRepo.getSubWorkOrders(undefined, workOrderId as WorkOrderId)
      : this.adminRepo.getSubWorkOrders();
  }

  public async listWorkOrders(context: RequestContext, clientId?: string, projectId?: string): Promise<WorkOrder[]> {
    this.auth.require(context, PermissionCode.AdministrationRead);
    // [ONLINE-TRACE][OS-LIST] Diagnóstico temporário seguro — captura filtros
    // reais aplicados na listagem de OS para identificar divergência produção × teste.
    console.log("[ONLINE-TRACE][OS-LIST]", JSON.stringify({
      userId: context.userId,
      activeClientId: (context as any).activeClientId ?? null,
      requestedClientId: clientId ?? null,
      requestedProjectId: projectId ?? null,
      modeAll: !clientId && !(context as any).activeClientId
    }));
    let list: WorkOrder[];
    if (clientId) {
      // REGRESSÃO V3-B2: clientId explícito → valida acesso e retorna somente aquele Cliente.
      await this.assertUserHasAccessToClient(context, clientId);
      list = await this.adminRepo.getWorkOrders(clientId as ClientId);
    } else {
      // REGRESSÃO V3-B1/B2: sem clientId explícito = TODOS os clientes autorizados.
      // NÃO usar activeClientId como fallback (causa raiz confirmada no 403f86e).
      const authorizedClients = await this.listAuthorizedClients(context);
      const seenIds = new Set<string>();
      const allWos: WorkOrder[] = [];
      for (const client of authorizedClients) {
        const wos = await this.adminRepo.getWorkOrders(client.id as ClientId);
        for (const wo of wos) {
          if (!seenIds.has(wo.id)) {
            seenIds.add(wo.id);
            allWos.push(wo);
          }
        }
      }
      list = allWos;
    }
    if (projectId) {
      list = list.filter(wo => wo.projectId === projectId);
    }
    // [ONLINE-TRACE][OS-LIST-RESULT] Contagem e IDs retornados após filtro.
    console.log("[ONLINE-TRACE][OS-LIST-RESULT]", JSON.stringify({
      resultCount: list.length,
      workOrderIds: list.map(wo => wo.id),
      distinctClientIds: [...new Set(list.map(wo => wo.clientId))]
    }));
    return list;
  }

  public async createWorkOrder(
    context: RequestContext,
    data: { clientId?: string; projectId?: string | null; areaId?: string | null; description?: string | null; startDate?: string | null; endDate?: string | null }
  ): Promise<WorkOrder> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    // [ONLINE-TRACE][OS-CREATE] Diagnóstico temporário seguro — captura clientId
    // do formulário vs activeClientId para identificar sobrescrita indevida.
    console.log("[ONLINE-TRACE][OS-CREATE]", JSON.stringify({
      userId: context.userId,
      activeClientId: (context as any).activeClientId ?? null,
      formClientId: data.clientId ?? null,
      resolvedClientId: data.clientId || (context as any).activeClientId || null
    }));
    const targetClientId = data.clientId || context.activeClientId;
    if (!targetClientId || !targetClientId.trim()) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O CLIENTE.");
    }

    const client = await this.assertUserHasAccessToClient(context, targetClientId);

    // CLIENTE → OS: obrigatório. CLIENTE → OS → PROJETO: opcional na criação.
    // Uma OS pode existir sem Projeto e ser vinculada depois — inclusive a
    // vários — via RdoService.linkProjectToWorkOrder (regra registrada no
    // CLAUDE.md). Só valida o Projeto quando um É informado.
    const targetProjectId = data.projectId?.trim() || null;

    if (targetProjectId && this.projectChecker) {
      const proj = await this.projectChecker.getProject(targetProjectId);
      if (proj && proj.clientId && proj.clientId !== targetClientId) {
        throw new AppError(ErrorCode.Validation, "O Projeto selecionado não pertence ao Cliente informado.", {
          safeMessage: "O Projeto selecionado pertence a outro Cliente. Operação bloqueada por isolamento multi-tenant."
        });
      }
    }

    if (data.areaId) {
      const area = (await this.adminRepo.getAreas(targetClientId as ClientId)).find(a => a.id === data.areaId);
      if (!area) {
        throw new AppError(ErrorCode.Validation, "A Área selecionada não pertence ao cliente informado.");
      }
    }

    const clientPrefix = client.name.substring(0, 3).toUpperCase();
    const existing = await this.adminRepo.getWorkOrders(targetClientId as ClientId);
    const count = existing.length + 1;
    const code = `${clientPrefix}-${count.toString().padStart(4, '0')}`;

    const now = new Date().toISOString();
    const id = `wo-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const wo: WorkOrder = {
      id: id as WorkOrderId,
      clientId: targetClientId as ClientId,
      projectId: targetProjectId,
      areaId: (data.areaId as AreaId) || null,
      code,
      description: data.description ? data.description.trim() : null,
      status: 'OPEN',
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      createdAt: now,
      updatedAt: now
    };

    await this.adminRepo.saveWorkOrder(wo);
    // [ONLINE-TRACE][OS-CREATE-PERSISTED] Diagnóstico temporário seguro —
    // confirma o clientId realmente gravado no banco após save.
    console.log("[ONLINE-TRACE][OS-CREATE-PERSISTED]", JSON.stringify({
      workOrderId: wo.id,
      persistedClientId: wo.clientId,
      persistedCode: wo.code,
      formClientId: data.clientId ?? null,
      activeClientId: (context as any).activeClientId ?? null,
      mismatch: data.clientId && data.clientId !== wo.clientId
    }));
    return wo;
  }

  public async updateWorkOrder(context: RequestContext, wo: WorkOrder): Promise<WorkOrder> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    // Projeto é opcional aqui também (mesma regra da criação — CLAUDE.md):
    // editar uma OS para remover/nunca ter tido Projeto é uma operação válida.
    const projectId = wo.projectId?.trim() || null;
    if (projectId && this.projectChecker) {
      const proj = await this.projectChecker.getProject(projectId);
      if (proj && proj.clientId && proj.clientId !== wo.clientId) {
        throw new AppError(ErrorCode.Validation, "O Projeto selecionado não pertence ao Cliente informado.", {
          safeMessage: "O Projeto selecionado pertence a outro Cliente. Operação bloqueada por isolamento multi-tenant."
        });
      }
    }
    const updated: WorkOrder = {
      ...wo,
      projectId,
      updatedAt: new Date().toISOString()
    };
    await this.adminRepo.saveWorkOrder(updated);
    return updated;
  }

  public async inactivateWorkOrder(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    await this.adminRepo.inactivateWorkOrder(context.activeClientId, id as WorkOrderId);
  }

  public async deleteWorkOrder(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    
    // Verifica dependências diretas conhecidas neste serviço (Sub-OS e Service Fronts)
    const subWos = await this.adminRepo.getSubWorkOrders(context.activeClientId, id as WorkOrderId);
    if (subWos.length > 0) {
      throw new AppError(ErrorCode.Conflict, `Esta OS possui ${subWos.length} Sub-OS vinculadas. A exclusão física foi bloqueada para proteger o histórico. Utilize a Inativação.`);
    }

    const sfs = await this.adminRepo.getServiceFronts(context.activeClientId, id as WorkOrderId);
    if (sfs.length > 0) {
      throw new AppError(ErrorCode.Conflict, `Esta OS possui ${sfs.length} Frente(s) de Serviço vinculada(s). A exclusão física foi bloqueada.`);
    }

    await this.adminRepo.deleteWorkOrder(id as WorkOrderId);
    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "WorkOrder",
        entityId: id,
        previousState: null,
        newState: null,
        metadata: { action: "WORK_ORDER_DELETED" }
      }).catch(() => {});
    }
  }

  // ---------------------------------------------------------------------------
  // SUB-OS (SUB WORK ORDERS)
  // ---------------------------------------------------------------------------
  public async listSubWorkOrders(context: RequestContext, workOrderId?: string, clientId?: string): Promise<SubWorkOrder[]> {
    this.requireAny(context, [PermissionCode.SubWorkOrderRead, PermissionCode.AdministrationRead, PermissionCode.AdministrationManage]);
    // REGRA V4-R: SubWorkOrders segue a mesma semântica de WorkOrders.
    // COM clientId: valida acesso e retorna somente aquele Cliente.
    // SEM clientId: união dos Clientes autorizados (não activeClientId).
    if (clientId) {
      await this.assertUserHasAccessToClient(context, clientId);
      return this.adminRepo.getSubWorkOrders(clientId as ClientId, workOrderId as WorkOrderId);
    }
    const authorizedClients = await this.listAuthorizedClients(context);
    const seenIds = new Set<string>();
    const allSubs: SubWorkOrder[] = [];
    for (const client of authorizedClients) {
      const subs = await this.adminRepo.getSubWorkOrders(client.id as ClientId, workOrderId as WorkOrderId);
      for (const sub of subs) {
        if (!seenIds.has(sub.id)) {
          seenIds.add(sub.id);
          allSubs.push(sub);
        }
      }
    }
    return allSubs;
  }

  public async createSubWorkOrder(
    context: RequestContext,
    data: { clientId?: string; workOrderId: string; projectId?: string | null; unitAreaId?: string | null; professionalId: string; description?: string | null; startDate?: string | null; endDate?: string | null }
  ): Promise<SubWorkOrder> {
    this.requireAny(context, [PermissionCode.SubWorkOrderCreate, PermissionCode.AdministrationManage]);

    if (!data.professionalId) {
      throw new AppError(ErrorCode.Validation, "SELECIONE O PROFISSIONAL RESPONSÁVEL PELA SUB-OS.");
    }

    const allWos = await this.adminRepo.getWorkOrders();
    const wo = allWos.find(w => w.id === data.workOrderId);
    if (!wo) throw new AppError(ErrorCode.NotFound, "Ordem de Serviço (OS) não encontrada.");

    const effectiveClientId = wo.clientId;
    if (data.clientId && data.clientId !== effectiveClientId) {
      throw new AppError(ErrorCode.Validation, "A OS selecionada não pertence ao cliente informado.");
    }

    await this.assertUserHasAccessToClient(context, effectiveClientId);

    const existingSub = await this.adminRepo.getSubWorkOrders(effectiveClientId as ClientId, data.workOrderId as WorkOrderId);
    const count = existingSub.length + 1;
    const code = `${wo.code}-0${count}`;

    const now = new Date().toISOString();
    const id = `subwo-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const swo: SubWorkOrder = {
      id,
      clientId: effectiveClientId,
      workOrderId: data.workOrderId as WorkOrderId,
      // A Sub-OS pertence a um Projeto DENTRO da OS — quando a tela informa
      // qual é, ele é gravado; do contrário fica nulo como nos registros antigos.
      projectId: data.projectId || null,
      code,
      professionalId: data.professionalId as ProfessionalId,
      unitAreaId: data.unitAreaId || null,
      description: data.description ? data.description.trim() : null,
      status: 'OPEN',
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      createdAt: now,
      updatedAt: now
    };

    await this.adminRepo.saveSubWorkOrder(swo);

    if (swo.professionalId && this.identityRepo) {
      try {
        const professional = await this.identityRepo.getProfessional(swo.professionalId);
        if (professional && professional.userId) {
          await notificationService.createNotification({
            userId: professional.userId,
            type: 'INFO',
            event: 'SUB_OS_ASSIGNED',
            title: 'Nova Sub-OS atribuída',
            message: `Você foi designado para a Sub-OS ${swo.code}.`,
            entityType: 'SubWorkOrder',
            entityId: swo.id,
            clientId: swo.clientId,
            idempotencyKey: `SUB_OS_ASSIGNED_${swo.id}`
          });
        }
      } catch (err) {
        console.warn('Falha ao gerar notificação para Sub-OS', err);
      }
    }

    return swo;
  }

  public async updateSubWorkOrder(context: RequestContext, swo: SubWorkOrder): Promise<SubWorkOrder> {
    this.requireAny(context, [PermissionCode.SubWorkOrderUpdate, PermissionCode.AdministrationManage]);

    if (swo.clientId && swo.workOrderId) {
      const allWos = await this.adminRepo.getWorkOrders();
      const wo = allWos.find(w => w.id === swo.workOrderId);
      if (!wo) throw new AppError(ErrorCode.NotFound, "Ordem de Serviço (OS) não encontrada.");
      if (wo.clientId !== swo.clientId) {
        throw new AppError(ErrorCode.Validation, "A OS selecionada não pertence ao cliente informado.");
      }
    }

    if (swo.professionalId) {
      const professional = await this.identityRepo.getProfessional(swo.professionalId);
      if (!professional || professional.deletedAt || professional.status !== ProfessionalStatus.Active) {
        throw new AppError(ErrorCode.Validation, "Profissional inválido ou inativo.");
      }
    }

    const updated: SubWorkOrder = {
      ...swo,
      updatedAt: new Date().toISOString()
    };
    await this.adminRepo.saveSubWorkOrder(updated);
    return updated;
  }

  public async inactivateSubWorkOrder(context: RequestContext, id: string): Promise<void> {
    this.requireAny(context, [PermissionCode.SubWorkOrderDelete, PermissionCode.AdministrationManage]);
    await this.adminRepo.inactivateSubWorkOrder(context.activeClientId, id);
  }

  public async deleteSubWorkOrder(context: RequestContext, id: string): Promise<void> {
    this.requireAny(context, [PermissionCode.SubWorkOrderDelete, PermissionCode.AdministrationManage]);
    const sfs = await this.adminRepo.getServiceFronts(context.activeClientId);
    const hasSfs = sfs.some(sf => sf.subWorkOrderId === id);
    if (hasSfs) {
      throw new AppError(ErrorCode.Conflict, "Não é possível excluir a Sub-OS pois existem Frentes de Serviço vinculadas. Utilize a opção Cancelar.");
    }
    await this.adminRepo.deleteSubWorkOrder(id);
    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "SubWorkOrder",
        entityId: id,
        previousState: null,
        newState: null,
        metadata: { action: "SUB_WORK_ORDER_DELETED" }
      }).catch(() => {});
    }
  }

  // ---------------------------------------------------------------------------
  // SERVICE FRONTS
  // ---------------------------------------------------------------------------
  public async listServiceFronts(context: RequestContext, workOrderId?: string, clientId?: string): Promise<ServiceFront[]> {
    this.auth.require(context, PermissionCode.AdministrationRead);
    const targetClient = clientId || context.activeClientId;
    return this.adminRepo.getServiceFronts(targetClient ? (targetClient as ClientId) : undefined, workOrderId as WorkOrderId);
  }

  public async createServiceFront(
    context: RequestContext,
    data: { clientId?: string; workOrderId: string; subWorkOrderId?: string | null; name: string }
  ): Promise<ServiceFront> {
    this.auth.require(context, PermissionCode.AdministrationManage);

    const allWos = await this.adminRepo.getWorkOrders();
    const wo = allWos.find(w => w.id === data.workOrderId);
    if (!wo) throw new AppError(ErrorCode.NotFound, "Ordem de Serviço (OS) não encontrada.");

    const effectiveClientId = wo.clientId;
    if (data.clientId && data.clientId !== effectiveClientId) {
      throw new AppError(ErrorCode.Validation, "A OS selecionada não pertence ao cliente informado.");
    }

    await this.assertUserHasAccessToClient(context, effectiveClientId);

    const now = new Date().toISOString();
    const id = `sf-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const sf: ServiceFront = {
      id: id as ServiceFrontId,
      clientId: effectiveClientId,
      workOrderId: data.workOrderId as WorkOrderId,
      subWorkOrderId: data.subWorkOrderId || null,
      name: data.name.trim(),
      status: 'ACTIVE',
      createdAt: now,
      updatedAt: now
    };

    await this.adminRepo.saveServiceFront(sf);
    return sf;
  }

  public async updateServiceFront(context: RequestContext, sf: ServiceFront): Promise<ServiceFront> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    if (sf.clientId !== context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, "Acesso negado para outro cliente.");
    }
    const updated: ServiceFront = {
      ...sf,
      updatedAt: new Date().toISOString()
    };
    await this.adminRepo.saveServiceFront(updated);
    return updated;
  }

  public async inactivateServiceFront(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    await this.adminRepo.inactivateServiceFront(context.activeClientId, id as ServiceFrontId);
  }

  // ---------------------------------------------------------------------------
  // SIGNATORIES
  // ---------------------------------------------------------------------------
  public async listSignatories(context: RequestContext): Promise<Signatory[]> {
    this.auth.require(context, PermissionCode.AdministrationRead);
    return this.adminRepo.getSignatories(context.activeClientId);
  }

  public async createSignatory(
    context: RequestContext,
    data: { professionalId: string; roleDescription?: string | null }
  ): Promise<Signatory> {
    this.auth.require(context, PermissionCode.AdministrationManage);

    const existing = await this.adminRepo.getSignatories(context.activeClientId);
    if (existing.some(s => s.professionalId === data.professionalId && s.status === 'ACTIVE')) {
      throw new AppError(ErrorCode.Conflict, "Profissional já está cadastrado como signatário ativo.");
    }

    const now = new Date().toISOString();
    const id = `sig-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const sig: Signatory = {
      id: id as SignatoryId,
      clientId: context.activeClientId,
      professionalId: data.professionalId as ProfessionalId,
      roleDescription: data.roleDescription ? data.roleDescription.trim() : null,
      status: 'ACTIVE',
      createdAt: now,
      updatedAt: now
    };

    await this.adminRepo.saveSignatory(sig);
    return sig;
  }

  public async inactivateSignatory(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    const signatory = await this.findSignatoryById(id);
    if (!signatory) {
      throw new AppError(ErrorCode.NotFound, "Signatory not found", { safeMessage: "Signatário não encontrado." });
    }
    await this.adminRepo.inactivateSignatory(signatory.clientId, id as SignatoryId);
  }

  /**
   * Signatário por id, independente do Cliente ativo na sessão. Usa a porta
   * dedicada quando o repositório a oferece e, se não oferecer, varre os
   * Clientes — nunca assume que o registro é do Cliente selecionado na tela.
   */
  private async findSignatoryById(id: string): Promise<Signatory | null> {
    if (this.adminRepo.getSignatoryById) {
      return this.adminRepo.getSignatoryById(id as SignatoryId).catch(() => null);
    }
    const clients = await this.identityRepo.listClients();
    for (const client of clients) {
      const found = (await this.adminRepo.getSignatories(client.id)).find(s => String(s.id) === String(id));
      if (found) return found;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // USERS
  // ---------------------------------------------------------------------------
  public async listUsers(context: RequestContext): Promise<User[]> {
    this.auth.require(context, PermissionCode.IdentityRead);
    return this.identityRepo.listUsers();
  }

  /**
   * AUTENTICAÇÃO POR CREDENCIAL (ambiente sem backend HTTP: teste local).
   *
   * Propositalmente SEM `auth.require`: login é ato de PRÉ-autenticação — não
   * existe ainda um usuário autenticado cujas permissões pudessem ser
   * exigidas, e exigir identity.read aqui é justamente o que quebrava o
   * acesso. Não é bypass de RBAC: nada é lido ou alterado além da conferência
   * da credencial, e a senha nunca sai daqui (o retorno é o User que o
   * repositório já entrega às telas de Administração).
   *
   * Usa o MESMO `identityRepo` de createUser/updateUser, então todo usuário
   * cadastrado em Administração → Usuários entra por aqui, sem segunda fonte.
   */
  public async authenticateByCredentials(identifier: string, password: string): Promise<CredentialAuthentication> {
    const users = await this.identityRepo.listUsers();
    const result = await authenticateWithCredentials(users, identifier, password);
    // Credencial antiga (texto puro / `hash_<senha>`) entra uma última vez e
    // já sai regravada em PBKDF2. Sem isto o formato inseguro nunca sumiria
    // do cadastro. Se a regravação falhar, o acesso NÃO é negado — a senha
    // estava certa; a migração tenta de novo no próximo login.
    if (result.user && result.needsUpgrade) {
      try {
        await this.identityRepo.saveUser({
          ...result.user,
          password: undefined,
          passwordHash: await hashPassword(password)
        } as User);
      } catch { /* migração oportunista */ }
    }
    return result;
  }

  /**
   * PESSOAS E ACESSOS — a listagem, numa consulta agregada.
   *
   * Três leituras (Profissionais, Usuários, Clientes) e um cruzamento em
   * memória por índice. Nada de uma consulta por pessoa para descobrir a
   * conta, o PLH ou a assinatura (item 32).
   *
   * O payload é a projeção leve: sem imagem de assinatura, sem foto, sem
   * histórico. `hasSignature` é um booleano derivado de `signatureImageId` —
   * a imagem só é lida ao abrir a pessoa.
   */
  public async listPeopleDirectory(context: RequestContext): Promise<ProfessionalDirectoryView[]> {
    this.auth.require(context, PermissionCode.IdentityRead);
    const [professionals, users, clients] = await Promise.all([
      this.identityRepo.listProfessionals(),
      this.identityRepo.listUsers(),
      this.identityRepo.listClients()
    ]);
    return buildPeopleDirectory(professionals, users, clients);
  }

  /**
   * Pendências de migração entre Professional, User e Signatory (itens 36/37).
   *
   * Só RELATA. Nenhuma ficha é unida automaticamente: nome igual não é
   * identificação, e unir duas pessoas por engano dá a uma o acesso da outra.
   */
  public async listPeopleMigrationPendencies(context: RequestContext): Promise<PeopleMigrationPendency[]> {
    this.auth.require(context, PermissionCode.IdentityRead);
    const [professionals, users] = await Promise.all([
      this.identityRepo.listProfessionals(),
      this.identityRepo.listUsers()
    ]);
    return findPeopleMigrationPendencies(professionals, users);
  }

  /**
   * RESOLVER PENDÊNCIA: candidatos ao vínculo de uma conta órfã.
   *
   * Só propõe candidatos por identificação inequívoca — registro profissional
   * ou e-mail. Nome não entra: homônimo é comum, e unir duas pessoas por
   * engano dá a uma o acesso, a assinatura e a responsabilidade técnica da
   * outra. Mais de um candidato devolve revisão manual.
   */
  public async resolveUserProfessionalLink(context: RequestContext, userId: string): Promise<RelinkResolution> {
    this.auth.require(context, PermissionCode.IdentityRead);
    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user) {
      throw new AppError(ErrorCode.NotFound, "User not found", { safeMessage: "Conta de acesso não encontrada." });
    }
    const professionals = await this.identityRepo.listProfessionals();
    // O registro profissional da conta órfã não existe como campo de User;
    // quando houver, o chamador informa. Sem ele, resta o e-mail.
    return findRelinkCandidates(user, professionals, null);
  }

  /**
   * Grava o vínculo correto: User.professionalId → Professional existente.
   *
   * NÃO cria Professional, NÃO cria User, NÃO apaga nada. Preserva login,
   * perfil, permissões, assinatura e todo o histórico documental — o que
   * muda é só a referência que estava quebrada.
   */
  public async relinkUserToProfessional(context: RequestContext, userId: string, professionalId: string): Promise<User> {
    this.auth.require(context, PermissionCode.IdentityManage);

    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user || user.deletedAt) {
      throw new AppError(ErrorCode.NotFound, "User not found", { safeMessage: "Conta de acesso não encontrada." });
    }
    const professional = await this.identityRepo.getProfessional(professionalId as ProfessionalId);
    if (!professional || professional.deletedAt) {
      throw new AppError(ErrorCode.NotFound, "Professional not found", { safeMessage: "Profissional não encontrado no cadastro." });
    }
    // Um Professional pertence a UMA conta. Reapontar para um já tomado
    // transformaria a correção num sequestro de identidade.
    if (professional.userId && String(professional.userId) !== String(user.id)) {
      throw new AppError(ErrorCode.Conflict, "Professional already linked", {
        safeMessage: `${professional.name} já está vinculado a outra conta de acesso. Escolha outro cadastro ou desfaça o vínculo existente antes.`
      });
    }

    const previousProfessionalId = user.professionalId ? String(user.professionalId) : null;
    const now = new Date().toISOString();
    const updated = { ...user, professionalId: professional.id, version: (user.version || 1) + 1, updatedAt: now } as unknown as User;
    await this.identityRepo.saveUser(updated);
    if (!professional.userId) {
      await this.identityRepo.saveProfessional({ ...professional, userId: user.id, updatedAt: now } as unknown as Professional);
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.PersonIdentityRelinked,
        entityType: "User",
        entityId: String(user.id),
        previousState: { professionalId: previousProfessionalId },
        newState: { professionalId: String(professional.id) },
        metadata: {
          userId: String(user.id),
          previousProfessionalId,
          newProfessionalId: String(professional.id),
          professionalName: professional.name,
          resolvedBy: context.userId ?? null,
          resolvedAt: now
        }
      }).catch(() => {});
    }
    return updated;
  }

  /**
   * LIMPEZA DO `Professional.plhClientId` LEGADO.
   *
   * Desde 2026-09-24 a habilitação de PLH é decidida por
   * `Client.responsiblePlhProfessionalId`. O campo antigo deixou de ser lido,
   * mas os valores continuam gravados e podem discordar do Cliente.
   *
   *   DIVERGE — o Cliente nomeou OUTRA pessoa. O valor legado está
   *             definitivamente superado: nada depende dele e ninguém
   *             precisará dele de volta. É LIMPO quando `apply`.
   *
   *   ORFAO   — o Professional diz ser PLH de um Cliente que não nomeou
   *             ninguém. Aqui o valor legado é a ÚNICA pista de quem
   *             respondia. Limpar destruiria essa informação sem recuperação,
   *             e promovê-la seria o sistema escolher um PLH sozinho —
   *             proibido desde a seção 24. É apenas RELATADO.
   *
   * A coluna/campo NÃO é removido: some o valor anômalo, não a estrutura.
   * Roda onde quer que o repositório esteja (navegador ou backend), porque é
   * nos repositórios que o dado legado de fato vive.
   */
  public async cleanupLegacyPlhAnomalies(
    context: RequestContext,
    options: { apply: boolean } = { apply: false }
  ): Promise<{
    diverge: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string; clientResponsiblePlhProfessionalId: string }>;
    orphans: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string }>;
    cleared: number;
  }> {
    this.auth.require(context, PermissionCode.IdentityManage);

    const [professionals, clients] = await Promise.all([
      this.identityRepo.listProfessionals(),
      this.identityRepo.listClients()
    ]);
    const clientById = new Map(clients.map(c => [String(c.id), c]));

    const diverge: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string; clientResponsiblePlhProfessionalId: string }> = [];
    const orphans: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string }> = [];

    for (const professional of professionals) {
      if (professional.deletedAt || !professional.plhClientId) continue;
      const legacyClientId = String(professional.plhClientId);
      const client = clientById.get(legacyClientId);
      const responsible = client?.responsiblePlhProfessionalId ? String(client.responsiblePlhProfessionalId) : null;
      if (responsible === String(professional.id)) continue; // concordam

      const base = {
        professionalId: String(professional.id),
        professionalName: professional.name || String(professional.id),
        legacyClientId,
        clientName: client?.name || `(Cliente ${legacyClientId} não encontrado)`
      };
      if (responsible) diverge.push({ ...base, clientResponsiblePlhProfessionalId: responsible });
      else orphans.push(base);
    }

    let cleared = 0;
    if (options.apply) {
      for (const row of diverge) {
        const professional = professionals.find(p => String(p.id) === row.professionalId);
        if (!professional) continue;
        await this.identityRepo.saveProfessional({
          ...professional, plhClientId: null, updatedAt: new Date().toISOString()
        } as unknown as Professional);
        cleared++;
      }
      if (cleared > 0 && this.audit) {
        await this.audit.record(context, {
          action: AuditAction.Update,
          entityType: "Professional",
          entityId: "LEGACY_PLH_CLEANUP",
          previousState: null,
          newState: null,
          metadata: {
            action: "LEGACY_PLH_CLIENT_ID_CLEARED",
            cleared,
            orphansLeft: orphans.length,
            executedBy: context.userId ?? null,
            executedAt: new Date().toISOString()
          }
        }).catch(() => {});
      }
    }
    return { diverge, orphans, cleared };
  }

  /**
   * CRIAR ACESSO para um Professional que já existe (item 15).
   *
   * Cria SOMENTE o User e o amarra ao Professional informado. Nenhuma pessoa
   * nova é cadastrada — era assim que a mesma pessoa acabava em duplicado.
   */
  public async createAccessForProfessional(
    context: RequestContext,
    professionalId: string,
    data: { login: string; email?: string; password?: string; profileType: string; customPermissions?: readonly string[] }
  ): Promise<User> {
    this.auth.require(context, PermissionCode.UserManage);

    const professional = await this.identityRepo.getProfessional(professionalId as ProfessionalId);
    if (!professional || professional.deletedAt) {
      throw new AppError(ErrorCode.NotFound, "Professional not found", { safeMessage: "Profissional não encontrado." });
    }

    const users = await this.identityRepo.listUsers();
    const existing = users.find(u => !u.deletedAt && String(u.professionalId || "") === String(professionalId));
    if (existing) {
      throw new AppError(ErrorCode.Conflict, "Professional already has an account", {
        safeMessage: `${professional.name} já possui uma conta de acesso ('${existing.login || existing.username || existing.email}').`
      });
    }

    const user = await this.createUser(context, {
      name: professional.name,
      email: (data.email || professional.email || "").trim(),
      login: data.login,
      ...(data.password ? { password: data.password } : {}),
      profileType: data.profileType,
      ...(data.customPermissions ? { customPermissions: [...data.customPermissions] } : {}),
      professionalId: String(professional.id)
    } as never);

    // Vínculo nos dois sentidos, para que nenhuma leitura futura precise
    // adivinhar qual lado é o oficial.
    if (!professional.userId) {
      await this.identityRepo.saveProfessional({ ...professional, userId: user.id, updatedAt: new Date().toISOString() } as Professional);
    }
    return user;
  }

  /**
   * REDEFINIR A SENHA de uma conta, pelo administrador.
   *
   * Recebe a senha em texto e a deriva pelo mecanismo oficial (PBKDF2), como
   * qualquer outro caminho: nada de gravar texto puro nem de inventar um
   * segundo formato. O campo legado `password` é apagado de passagem, para
   * que a conta não volte a ter a senha legível.
   *
   * A auditoria registra QUE a senha mudou, QUEM mudou e QUANDO — nunca a
   * senha, nem o hash, nem parte deles.
   */
  public async resetUserPassword(context: RequestContext, userId: string, newPassword: string): Promise<void> {
    this.auth.require(context, PermissionCode.UserManage);

    const senha = (newPassword || "").trim();
    if (senha.length < 8) {
      throw new AppError(ErrorCode.Validation, "Password too short", {
        safeMessage: "A nova senha precisa ter pelo menos 8 caracteres."
      });
    }
    await this.assertPasswordPolicy(senha);

    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user || user.deletedAt) {
      throw new AppError(ErrorCode.NotFound, "User not found", { safeMessage: "Conta de acesso não encontrada." });
    }

    await this.identityRepo.saveUser({
      ...user,
      password: undefined,
      passwordHash: await hashPassword(senha),
      version: (user.version || 1) + 1,
      updatedAt: new Date().toISOString()
    } as unknown as User);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "User",
        entityId: String(user.id),
        previousState: null,
        newState: null,
        metadata: {
          action: "USER_PASSWORD_RESET",
          userId: String(user.id),
          login: user.login || user.username || null,
          changedBy: context.userId ?? null,
          changedAt: new Date().toISOString()
        }
      }).catch(() => {});
    }
  }

  /**
   * Regrava a senha já derivada de um usuário. Existe para a migração
   * oportunista do login de homologação, que autentica pelo mesmo
   * `passwordMatches` mas persiste pelo servidor. Recebe o valor JÁ
   * derivado — senha em texto não entra por aqui — e recusa qualquer
   * outro formato, para não virar uma porta de gravar senha fraca.
   */
  public async upgradeStoredPassword(userId: string, hashedPassword: string): Promise<void> {
    if (!isHashedPassword(hashedPassword)) {
      throw new AppError(ErrorCode.Validation, "Formato de senha inválido para regravação.");
    }
    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user) return;
    await this.identityRepo.saveUser({ ...user, password: undefined, passwordHash: hashedPassword } as User);
  }

  /** Releitura da sessão guardada (mesma justificativa de pré-autenticação): só devolve usuário ATIVO. */
  public async findUserForAuthentication(userId: string): Promise<User | null> {
    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user || user.deletedAt || user.status !== UserStatus.Active) return null;
    return user;
  }

  /** Profissional vinculado ao usuário autenticado (pré-autenticação: só o vínculo, nada de cadastro). */
  public async listProfessionalsForAuthentication(userId: string): Promise<Professional[]> {
    const professionals = await this.identityRepo.listProfessionals();
    return professionals.filter(p => String(p.userId || "") === String(userId) && p.status === ProfessionalStatus.Active && !p.deletedAt);
  }

  /**
   * AS PERMISSÕES DE PLH QUE ESTA PESSOA REALMENTE TEM.
   *
   * ────────────────────────────────────────────────────────────────────
   * Defeito corrigido: ninguém recebia estas permissões na vida real.
   * ────────────────────────────────────────────────────────────────────
   *
   * `PLH_SPECIFIC_PERMISSIONS` (analisar, aprovar, devolver, assinar) não
   * pertence a nenhum perfil — e a única atribuição existente estava no
   * `homologation-seed.ts`, para um usuário fixo `plh1`. Consequência: o
   * Administrador cadastrava o Engenheiro, nomeava-o PLH responsável do
   * Cliente, criava a conta dele — e a conta nascia sem NENHUMA permissão de
   * PLH. Em ME, LP, PM, USC, RI e AVR os botões INICIAR ANÁLISE, ENVIAR PARA
   * CORREÇÃO e APROVAR RELATÓRIO simplesmente não apareciam, sem erro e sem
   * explicação.
   *
   * A concessão é DERIVADA da fonte única (`Client.responsiblePlhProfessionalId`,
   * seção 24) e recalculada a cada sessão. Gravá-la como `customPermissions`
   * no usuário criaria uma segunda fonte: ao substituir o PLH do Cliente, o
   * antigo continuaria com as permissões na conta.
   *
   * ISTO NÃO AFROUXA NADA. A permissão diz apenas "esta pessoa atua como PLH
   * em algum lugar". QUAL documento ela pode aprovar continua sendo decidido
   * por documento, contra o CLIENTE DO DOCUMENTO
   * (`TechnicalReviewService.requireAssignedPlh` + `canActAsPlh`) — um PLH da
   * Cliente A segue recusado na Cliente B.
   */
  public async plhPermissionsForAuthentication(professionalId: string | null | undefined): Promise<readonly string[]> {
    const id = (professionalId ?? "").trim();
    if (!id) return [];

    try {
      const professional = await this.identityRepo.getProfessional(id as never);
      if (!professional) return [];

      const clients = await this.identityRepo.listClients();
      const responsibleFor = clients.filter(c =>
        c.deletedAt === null &&
        c.status === ClientStatus.Active &&
        String((c as { responsiblePlhProfessionalId?: string | null }).responsiblePlhProfessionalId ?? "") === id
      );
      if (responsibleFor.length === 0) return [];

      // A habilitação técnica é conferida contra um dos Clientes que o
      // nomearam: ativo, Engenheiro e com assinatura cadastrada. Sem isso a
      // pessoa é responsável no cadastro mas não pode assinar — e conceder a
      // permissão aqui só adiaria a recusa para o momento da aprovação.
      const qualified = isPlhQualifiedProfessional(professional, id as never);
      if (!qualified.allowed) return [];

      return PLH_SPECIFIC_PERMISSIONS.map(String);
    } catch {
      // Não foi possível apurar: nenhuma permissão extra. Falha fechada.
      return [];
    }
  }

  public async createUser(
    context: RequestContext,
    data: {
      name: string;
      email: string;
      login?: string;
      username?: string;
      password?: string;
      roleCodes?: string[];
      clientId?: string;
      profileType?: string;
    }
  ): Promise<User> {
    this.auth.require(context, PermissionCode.IdentityManage);

    const normalizedEmail = data.email.trim().toLowerCase();
    const existing = await this.identityRepo.listUsers();
    if (existing.some(u => u.email && u.email.trim().toLowerCase() === normalizedEmail)) {
      throw new AppError(ErrorCode.Conflict, `Usuário com e-mail '${data.email}' já existe.`);
    }

    const rawLogin = (data.login || data.username || normalizedEmail.split('@')[0] || '').trim().toLowerCase();
    const normalizedLogin = rawLogin.replace(/\s+/g, '.');
    if (normalizedLogin && existing.some(u => (u.login?.trim().toLowerCase() === normalizedLogin || u.username?.trim().toLowerCase() === normalizedLogin))) {
      throw new AppError(ErrorCode.Conflict, `O login '${normalizedLogin}' já está em uso por outro usuário.`);
    }

    const now = new Date().toISOString();
    const id = `usr-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    if (data.password && data.password.trim()) await this.assertPasswordPolicy(data.password.trim());
    const userPassword = (data.password && data.password.trim()) ? data.password.trim() : '123456';
    // A senha vai para o cadastro só derivada. O campo `password` (texto
    // puro) não é mais gravado: quem lesse o repositório lia a senha.
    const userPasswordHash = await hashPassword(userPassword);

    const user: User = {
      id: id as UserId,
      name: data.name.trim(),
      email: normalizedEmail,
      login: normalizedLogin,
      username: normalizedLogin,
      passwordHash: userPasswordHash,
      status: UserStatus.Active,
      authenticationId: `auth_${id}`,
      // Without this, a newly created user has no profileType at all and
      // falls back to "TECHNICIAN" everywhere the role is displayed or
      // resolved (login-page.ts, topbar.ts, admin-users-page.ts) — the root
      // cause behind users created as e.g. Engineer showing up as Técnico
      // (Bloco 5, 2026-09-14). `data.profileType` must be set explicitly by
      // the caller at creation time; it is a distinct concept from
      // `roleCodes` below (which only feeds the separate, not-yet-connected
      // multi-tenant UserClientAccess record).
      ...(data.profileType ? { profileType: data.profileType } : {}),
      lastLoginAt: null,
      lastAccessAt: null,
      version: 1,
      createdAt: now as any,
      updatedAt: now as any,
      deletedAt: null,
      deletedBy: null,
      deletionReason: null
    };

    await this.identityRepo.saveUser(user);

    // Grant access to current client by default if requested
    const targetClient = data.clientId || context.activeClientId;
    if (targetClient && data.roleCodes && data.roleCodes.length > 0) {
      const accessId = `acc-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
      const access: UserClientAccess = {
        id: accessId as UserClientAccessId,
        userId: user.id,
        clientId: targetClient as ClientId,
        roleIds: data.roleCodes as RoleId[],
        status: UserClientAccessStatus.Active,
        version: 1,
        createdAt: now as any,
        updatedAt: now as any,
        deletedAt: null,
        deletedBy: null,
        deletionReason: null
      };
      await this.identityRepo.saveAccess(access);
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "User",
        entityId: user.id,
        previousState: null,
        newState: { name: user.name, email: user.email, login: user.login || null, status: user.status },
        metadata: { action: "USER_CREATED" }
      }).catch(() => {});
    }

    return user;
  }

  // ---------------------------------------------------------------------------
  // USER PROFILE PHOTO (Bloco 21)
  // Bound to the userId, stored apart from the professional's signature image,
  // and gated by the permission that already governs editing users — no new
  // permission is introduced.
  // ---------------------------------------------------------------------------

  public async setUserProfileImage(
    context: RequestContext,
    userId: string,
    image: { dataUrl: string; fileName?: string; mimeType?: string; sizeBytes?: number }
  ): Promise<User> {
    this.auth.require(context, PermissionCode.IdentityManage);

    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user) {
      throw new AppError(ErrorCode.NotFound, "Usuário não encontrado.", { safeMessage: "Usuário não encontrado." });
    }

    const errors = validateProfileImage(image);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid profile image: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    const replacing = !!(await this.identityRepo.getUserProfileImage(userId));
    const imageId = await this.identityRepo.saveUserProfileImage(userId, image.dataUrl);

    const updated: User = { ...user, profileImageId: imageId, updatedAt: new Date().toISOString() as any };
    await this.identityRepo.saveUser(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "User",
        entityId: userId,
        previousState: null,
        newState: null,
        metadata: {
          action: replacing ? "USER_PROFILE_IMAGE_REPLACED" : "USER_PROFILE_IMAGE_ADDED",
          userId,
          changedBy: context.userId ?? null
        }
      }).catch(() => {});
    }

    return updated;
  }

  public async getUserProfileImage(context: RequestContext, userId: string): Promise<string | null> {
    this.auth.require(context, PermissionCode.IdentityRead);
    return this.identityRepo.getUserProfileImage(userId);
  }

  /**
   * Foto de quem está autenticado. Não exige `identity.read`: o usuário é o
   * dono do dado e o alvo é sempre `context.userId` — nunca um id enviado
   * pela tela. Existe para o topbar não precisar mais fabricar um contexto
   * com permissões que o usuário não tem só para mostrar o próprio avatar.
   */
  public async getOwnProfileImage(context: RequestContext): Promise<string | null> {
    const userId = context.userId ? String(context.userId) : "";
    if (!userId) return null;
    return this.identityRepo.getUserProfileImage(userId);
  }

  public async removeUserProfileImage(context: RequestContext, userId: string): Promise<User> {
    this.auth.require(context, PermissionCode.IdentityManage);

    const user = await this.identityRepo.getUser(userId as UserId);
    if (!user) {
      throw new AppError(ErrorCode.NotFound, "Usuário não encontrado.", { safeMessage: "Usuário não encontrado." });
    }

    // Removes only the photo — never the user.
    await this.identityRepo.deleteUserProfileImage(userId);
    const updated: User = { ...user, profileImageId: null, updatedAt: new Date().toISOString() as any };
    await this.identityRepo.saveUser(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "User",
        entityId: userId,
        previousState: null,
        newState: null,
        metadata: { action: "USER_PROFILE_IMAGE_REMOVED", userId, changedBy: context.userId ?? null }
      }).catch(() => {});
    }

    return updated;
  }

  public async updateUser(context: RequestContext, user: User): Promise<User> {
    this.auth.require(context, PermissionCode.IdentityManage);
    const existing = await this.identityRepo.getUser(user.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Usuário não encontrado.");
    }

    // Verify login collision if changed
    if (user.login) {
      const targetLogin = user.login.trim().toLowerCase();
      const allUsers = await this.identityRepo.listUsers();
      const collision = allUsers.find(u => u.id !== user.id && (u.login?.trim().toLowerCase() === targetLogin || u.username?.trim().toLowerCase() === targetLogin));
      if (collision) {
        throw new AppError(ErrorCode.Conflict, `O login '${targetLogin}' já está em uso por outro usuário.`);
      }
    }

    const updated = {
      ...existing,
      ...user,
      /**
       * O VÍNCULO COM O PROFISSIONAL NÃO SE TROCA POR AQUI.
       *
       * `{...existing, ...user}` espalha o payload inteiro, e com ele
       * `professionalId` — trocá-lo muda de quem são os documentos já
       * emitidos, a assinatura e a responsabilidade técnica da pessoa.
       * Isso tem ato próprio, `relinkUserToProfessional`, que confere o
       * Professional de destino e audita o antes e o depois
       * (`PERSON_IDENTITY_RELINKED`, §30). Por aqui a troca passava sem
       * conferência nenhuma e nem sequer aparecia na auditoria, que não
       * registrava o campo.
       *
       * É a mesma proteção que `updateProfessional` já dava ao
       * `userId` — faltava o lado espelho.
       */
      professionalId: existing.professionalId,
      login: user.login ? user.login.trim().toLowerCase() : existing.login,
      username: user.login ? user.login.trim().toLowerCase() : existing.username,
      // Senha nova chega em texto, é derivada aqui e o texto é descartado.
      password: undefined,
      passwordHash: user.password && user.password.trim()
        ? (await this.assertPasswordPolicy(user.password.trim()), await hashPassword(user.password.trim()))
        : existing.passwordHash,
      version: (existing.version || 1) + 1,
      updatedAt: new Date().toISOString()
    } as any;

    await this.identityRepo.saveUser(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "User",
        entityId: user.id,
        previousState: { name: existing.name, login: existing.login || null, status: existing.status, profileType: (existing as any).profileType, customPermissions: existing.customPermissions ?? null, professionalId: existing.professionalId ?? null },
        newState: { name: updated.name, login: updated.login || null, status: updated.status, profileType: updated.profileType, customPermissions: updated.customPermissions ?? null, professionalId: updated.professionalId ?? null },
        metadata: { action: "USER_UPDATED" }
      }).catch(() => {});

      if ((existing as any).profileType !== updated.profileType) {
        await this.audit.record(context, {
          action: AuditAction.Update,
          entityType: "User",
          entityId: user.id,
          previousState: null,
          newState: null,
          metadata: {
            action: "USER_PROFILE_CHANGED",
            oldProfile: (existing as any).profileType,
            newProfile: updated.profileType
          }
        }).catch(() => {});
      }

      if (JSON.stringify(existing.customPermissions) !== JSON.stringify(updated.customPermissions)) {
        await this.audit.record(context, {
          action: AuditAction.Update,
          entityType: "User",
          entityId: user.id,
          previousState: null,
          newState: null,
          metadata: {
            action: "USER_PERMISSIONS_CHANGED",
            oldPermissions: existing.customPermissions ?? null,
            newPermissions: updated.customPermissions ?? null
          }
        }).catch(() => {});
      }
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'USER', id: updated.id } }));
    }

    return updated;
  }

  public async inactivateUser(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    await this.identityRepo.inactivateUser(id as UserId);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Inactivate,
        entityType: "User",
        entityId: id,
        previousState: null,
        newState: { status: "INACTIVE" },
        metadata: { action: "USER_INACTIVATED" }
      }).catch(() => {});
    }
  }

  public async deleteUser(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    
    const profs = await this.identityRepo.listProfessionals();
    const hasProfessional = profs.some(p => p.userId === id);
    if (hasProfessional) {
      throw new AppError(ErrorCode.Conflict, "Não é possível excluir o usuário pois ele possui um perfil de Profissional vinculado. Utilize a opção Inativar.");
    }
    await this.identityRepo.deleteUser(id as UserId);
    
    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "User",
        entityId: id,
        previousState: null,
        newState: null,
        metadata: { action: "USER_DELETED" }
      }).catch(() => {});
    }
  }

  // ---------------------------------------------------------------------------
  // PROFESSIONALS
  // ---------------------------------------------------------------------------
  /**
   * Lista de Profissionais visíveis conforme a matriz oficial de visibilidade:
   * - DEVELOPER / ADMIN / ENGINEER / DOCUMENT_TECH: vê todos os Profissionais do contexto autorizado.
   * - INSPECTOR / VIEWER: vê apenas o próprio Profissional vinculado (context.professionalId).
   */
  public async listVisibleProfessionals(
    context: RequestContext,
    options?: { role?: string; status?: string }
  ): Promise<Professional[]> {
    const hasBroadVisibility =
      context.permissions.has(PermissionCode.IdentityRead) ||
      context.permissions.has(PermissionCode.AdministrationRead) ||
      context.permissions.has(PermissionCode.ProjectManage) ||
      context.permissions.has(PermissionCode.EngineeringAccess) ||
      context.permissions.has(PermissionCode.InspectionReadAll);

    if (hasBroadVisibility) {
      // REGRA V4-C: PROFESSIONAL É GLOBAL. Listar SEM clientId.
      // Filtros permitidos: role, status. NUNCA Cliente.
      let profs = await this.identityRepo.listProfessionals();
      if (options?.role) profs = profs.filter(p => p.professionalType === options.role);
      if (options?.status) profs = profs.filter(p => p.status === options.status);
      return profs;
    }

    // Perfil com visibilidade restrita ao próprio profissional (INSPECTOR, VIEWER):
    if (!context.professionalId) {
      return [];
    }
    const own = await this.identityRepo.getProfessional(context.professionalId as ProfessionalId).catch(() => null);
    if (!own) return [];
    if (options?.role && own.professionalType !== options.role) return [];
    if (options?.status && own.status !== options.status) return [];
    return [own];
  }

  public async listProfessionals(
    context: RequestContext,
    options?: { role?: string; status?: string }
  ): Promise<Professional[]> {
    if (context.permissions.has(PermissionCode.IdentityRead)) {
      // REGRA V4-C: PROFESSIONAL É GLOBAL. Listar SEM clientId.
      // Filtros permitidos: role, status. NUNCA Cliente.
      let profs = await this.identityRepo.listProfessionals();
      if (options?.role) {
        profs = profs.filter(p => p.professionalType === options.role);
      }
      if (options?.status) {
        profs = profs.filter(p => p.status === options.status);
      }
      return profs;
    }

    if (context.permissions.has(PermissionCode.ProfessionalRead) || context.permissions.has(PermissionCode.ClientAccess)) {
      return this.listVisibleProfessionals(context, options);
    }

    this.auth.require(context, PermissionCode.IdentityRead);
    return [];
  }

  public async createProfessional(
    context: RequestContext,
    data: {
      name: string;
      email: string;
      functionTitle: string;
      companyId?: string | null;
      professionalType?: string;
      plhClientId?: string | null;
      cft?: string | null;
      crea?: string | null;
      endIso?: string | null;
      endIso9712?: string | null;
      snqc?: string | null;
      sncc?: string | null;
      userId?: string | null;
    }
  ): Promise<Professional> {
    this.auth.require(context, PermissionCode.IdentityManage);

    // PROFESSIONAL 1 → 0 ou 1 USER.
    //
    // A conta de acesso deixou de ser obrigatória para cadastrar uma pessoa.
    // Existe profissional que só precisa existir para fins documentais —
    // assinar um relatório, constar como Inspetor responsável — e nunca vai
    // entrar no sistema. Exigir um User para cadastrá-lo obrigava a criar
    // uma conta fantasma, ou a não cadastrar a pessoa. Quando o vínculo é
    // informado, tudo o que se validava continua valendo (conta existe, está
    // ativa e ainda não pertence a outro Professional).

    /**
     * O ENGENHEIRO/PLH É CADASTRADO SEM CLIENTE.
     *
     * Havia aqui `if (isEngineer && !data.plhClientId) throw "Selecione o
     * Cliente vinculado ao Engenheiro/PLH."`, que tornava **impossível
     * cadastrar um Engenheiro** sem escolher um Cliente na hora.
     *
     * A exigência contradizia a regra oficial em duas frentes. Primeiro, a
     * responsabilidade técnica vive no CLIENTE
     * (`Client.responsiblePlhProfessionalId`, CLAUDE.md §24) e é atribuída
     * em Administração → Clientes → PLH Responsável; o Professional não é o
     * lugar onde isso se decide. Segundo, um mesmo Engenheiro pode ser PLH
     * de vários Clientes (§31), de modo que um campo único no cadastro da
     * pessoa nunca poderia representar a realidade.
     *
     * Na prática o bloqueio criava o círculo vicioso da §30: para nomear
     * alguém PLH de um Cliente é preciso que a pessoa exista, e para a
     * pessoa existir exigia-se já ter um Cliente.
     *
     * A MESMA condição foi removida de `updateProfessional` (§39.3) e
     * sobreviveu aqui — editar um Engenheiro funcionava, criar não.
     *
     * `plhClientId` continua ACEITO quando informado (é elegibilidade
     * técnica legada, não responsabilidade) e continua sendo gravado logo
     * abaixo; o que deixou de existir é a obrigatoriedade.
     */
    const isEngineer = data.professionalType === 'ENGINEER' ||
      data.functionTitle?.toLowerCase().includes('plh') ||
      data.functionTitle?.toLowerCase().includes('engenheiro habilitado');

    const user = data.userId ? await this.identityRepo.getUser(data.userId as UserId) : null;
    if (data.userId) {
      if (!user || user.deletedAt) {
        throw new AppError(ErrorCode.NotFound, "Usuário não encontrado.");
      }
      if (user.status !== UserStatus.Active) {
        throw new AppError(ErrorCode.Validation, "O usuário selecionado não está ativo.");
      }
    }

    const existingProfessionals = await this.identityRepo.listProfessionals(context.activeClientId);
    if (user && existingProfessionals.some(p => p.userId === user.id && p.status === ProfessionalStatus.Active)) {
      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.Create,
          entityType: "Professional",
          entityId: "none",
          previousState: null,
          newState: { userId: user.id },
          metadata: { action: "DUPLICATE_USER_PROFESSIONAL_BLOCKED" }
        }).catch(() => {});
      }
      throw new AppError(ErrorCode.Conflict, "ESTE USUÁRIO JÁ POSSUI UM PROFISSIONAL VINCULADO.");
    }

    const now = new Date().toISOString();
    const id = `pro-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const endIsoVal = data.endIso ? data.endIso.trim() : (data.endIso9712 ? data.endIso9712.trim() : (data.snqc ? data.snqc.trim() : null));
    // REGRA V4-D: PROFESSIONAL É GLOBAL. Novos cadastros não recebem
    // ownership de Cliente. clientId permanece apenas como campo LEGADO.
    const professional: Professional = {
      id: id as ProfessionalId,
      clientId: null,
      userId: user ? user.id : null,
      // PROFESSIONAL É A FONTE MESTRE do nome e do e-mail profissional
      // (item 2). Antes estes campos eram copiados do User e os valores
      // enviados no cadastro eram descartados — o que, além de duplicar o
      // dado, tornava impossível cadastrar quem não tem conta.
      name: (data.name || user?.name || "").trim(),
      email: (data.email || user?.email || "").trim(),
      functionTitle: data.functionTitle.trim(),
      companyId: data.companyId ? (data.companyId as CompanyId) : null,
      professionalType: data.professionalType || "INSPECTOR",
      plhClientId: isEngineer ? (data.plhClientId?.trim() || null) : null,
      cft: data.cft ? data.cft.trim() : null,
      crea: data.crea ? data.crea.trim() : null,
      endIso: endIsoVal,
      endIso9712: endIsoVal,
      snqc: endIsoVal,
      sncc: data.sncc ? data.sncc.trim() : null,
      qualifications: [],
      digitalSignature: null,
      status: ProfessionalStatus.Active,
      version: 1,
      createdAt: now as any,
      updatedAt: now as any,
      deletedAt: null,
      deletedBy: null,
      deletionReason: null
    };

    await this.identityRepo.saveProfessional(professional);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "Professional",
        entityId: professional.id,
        previousState: null,
        newState: {
          name: professional.name,
          email: professional.email,
          userId: professional.userId,
          type: professional.professionalType ?? null,
          status: professional.status
        },
        metadata: { action: "PROFESSIONAL_CREATED", linkEvent: "USER_PROFESSIONAL_LINK_CREATED" }
      }).catch(() => {});
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'PROFESSIONAL', id: professional.id } }));
    }

    return professional;
  }

  public async updateProfessional(context: RequestContext, professional: Professional): Promise<Professional> {
    this.auth.require(context, PermissionCode.IdentityManage);

    /**
     * DOIS BLOQUEIOS REMOVIDOS AQUI (2026-09-25).
     *
     * 1. `professional.clientId !== context.activeClientId` recusava a
     *    edição sempre que o administrador estivesse com OUTRO Cliente
     *    selecionado na sessão. É o defeito da seção 28: o Cliente da sessão
     *    serve para navegar e filtrar, nunca para autorizar. Um Professional
     *    não pertence a um Cliente — pode ser PLH de vários (seção 42 do
     *    comando), e corrigir o telefone dele não é um ato "de Cliente".
     *    Quem autoriza é `identity.manage`, exigido acima.
     *
     * 2. `if (isEngineer && !professional.plhClientId) throw "Selecione o
     *    Cliente vinculado ao Engenheiro/PLH."` tornava IMPOSSÍVEL editar
     *    qualquer Engenheiro que não tivesse o campo legado preenchido —
     *    inclusive para corrigir um nome. A nomeação de PLH é do CLIENTE
     *    (`Client.responsiblePlhProfessionalId`, seção 24), e `plhClientId`
     *    é legado que nenhum gate lê desde 2026-09-24. Exigi-lo aqui pedia
     *    um dado que a tela, corretamente, não oferece mais.
     */

    const existing = await this.identityRepo.getProfessional(professional.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Profissional não encontrado.");
    }

    const isEngineer = professional.professionalType === 'ENGINEER' ||
      professional.functionTitle?.toLowerCase().includes('plh') ||
      professional.functionTitle?.toLowerCase().includes('engenheiro habilitado');

    // Se o cliente do PLH estiver mudando, garantir que ele não está ativo em projetos do cliente antigo
    if (isEngineer && professional.plhClientId && existing.plhClientId && professional.plhClientId !== existing.plhClientId) {
      if (this.projectChecker && (this.projectChecker as any).listProjects) {
        const allProjects = await (this.projectChecker as any).listProjects();
        const conflictingProjects = allProjects.filter((p: any) => p.engineerProfessionalId === professional.id && p.clientId !== professional.plhClientId);
        if (conflictingProjects.length > 0) {
          throw new AppError(ErrorCode.Validation, `O profissional está vinculado como responsável nos projetos: ${conflictingProjects.map((p: any) => p.code).join(', ')}. Resolva este vínculo antes de alterar o cliente do PLH.`);
        }
      }
    }

    const updated: Professional = {
      ...professional,
      /**
       * O VÍNCULO com o User é protegido: trocá-lo aqui mudaria a AUTORIA de
       * documentos já emitidos. Continua sendo ato administrativo próprio
       * (`relinkUserToProfessional`), auditado, e não um efeito colateral de
       * salvar o cadastro.
       */
      userId: existing.userId,
      /**
       * NOME e E-MAIL são editáveis mesmo com conta vinculada.
       *
       * Antes eram congelados quando existia `userId` ("Protect inherited"),
       * de quando o Professional COPIAVA esses dados do User. Desde a seção
       * 29 eles são do próprio cadastro profissional — e o congelamento
       * tornava impossível corrigir o nome de qualquer pessoa que tivesse
       * acesso ao sistema, inclusive um erro de digitação que sai impresso
       * nos documentos que ela assina.
       */
      name: professional.name,
      email: professional.email,
      plhClientId: isEngineer ? (professional.plhClientId?.trim() || null) : null,
      version: (existing.version || 1) + 1,
      updatedAt: new Date().toISOString() as any
    };

    await this.identityRepo.saveProfessional(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Professional",
        entityId: professional.id,
        previousState: { name: existing.name, status: existing.status },
        newState: { name: updated.name, status: updated.status },
        metadata: { action: "PROFESSIONAL_UPDATED" }
      }).catch(() => {});
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'PROFESSIONAL', id: updated.id } }));
    }

    return updated;
  }

  public async inactivateProfessional(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    await this.identityRepo.inactivateProfessional(id as ProfessionalId);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Inactivate,
        entityType: "Professional",
        entityId: id,
        previousState: null,
        newState: { status: "INACTIVE" },
        metadata: { action: "PROFESSIONAL_INACTIVATED" }
      }).catch(() => {});
    }
  }

  public async deleteProfessional(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    if ((this.identityRepo as any).deleteProfessional) {
      await (this.identityRepo as any).deleteProfessional(id as ProfessionalId);
    }
    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "Professional",
        entityId: id,
        previousState: null,
        newState: null,
        metadata: { action: "PROFESSIONAL_DELETED" }
      }).catch(() => {});
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'PROFESSIONAL', id } }));
    }
  }

  public async uploadProfessionalSignature(context: RequestContext, id: string, base64: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    const existing = await this.identityRepo.getProfessional(id as ProfessionalId);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Profissional não encontrado.");
    }

    const signatureImageId = await this.identityRepo.saveProfessionalSignature(id, base64);
    
    // Update the professional record with the reference
    const updated: Professional = {
      ...existing,
      signatureImageId,
      version: (existing.version || 1) + 1,
      updatedAt: new Date().toISOString() as any
    };
    await this.identityRepo.saveProfessional(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Professional",
        entityId: id,
        previousState: { signatureImageId: existing.signatureImageId || null },
        newState: { signatureImageId },
        metadata: { action: existing.signatureImageId ? "PROFESSIONAL_SIGNATURE_REPLACED" : "PROFESSIONAL_SIGNATURE_ADDED" }
      }).catch(() => {});
    }
  }

  public async getProfessionalSignature(context: RequestContext, id: string): Promise<string | null> {
    const existing = await this.identityRepo.getProfessional(id as ProfessionalId);
    if (!existing) return null;
    return this.identityRepo.getProfessionalSignature(id);
  }

  /**
   * ANEXAR A IMAGEM DE ASSINATURA DO PROFISSIONAL.
   *
   * O repositório já tinha `saveProfessionalSignature`, e o serviço expunha
   * `getProfessionalSignature` e `removeProfessionalSignature` — mas **não
   * o que grava**. Havia como ler e como apagar; não havia como cadastrar.
   *
   * O efeito era um bloqueio completo e silencioso: o gate de PLH exige
   * `signatureImageId` (§24, item 5; §31), a tela de Pessoas e Acessos
   * mostra a aba ASSINATURA e diz que ela "é cadastrada ao abrir a pessoa",
   * e não existia caminho nenhum para cadastrá-la. Nenhum Engenheiro podia
   * ser nomeado PLH de Cliente algum.
   *
   * VALIDAÇÃO DO FORMATO: só PNG e JPEG. O pdfmake, que assina os
   * documentos, **não renderiza SVG nem WEBP** — aceitar aqui produziria
   * uma assinatura que falha na hora de gerar o PDF, ou seja, o erro
   * apareceria no documento oficial em vez de no cadastro.
   */
  public async saveProfessionalSignature(context: RequestContext, id: string, dataUrl: string): Promise<Professional> {
    this.auth.require(context, PermissionCode.IdentityManage);
    const existing = await this.identityRepo.getProfessional(id as ProfessionalId);
    if (!existing || existing.deletedAt) {
      throw new AppError(ErrorCode.NotFound, "Profissional não encontrado.", {
        safeMessage: "Profissional não encontrado."
      });
    }

    const value = String(dataUrl ?? "").trim();
    if (!value) {
      throw new AppError(ErrorCode.Validation, "Empty signature", {
        safeMessage: "Selecione a imagem da assinatura."
      });
    }
    if (!/^data:image\/(png|jpe?g);base64,/i.test(value)) {
      throw new AppError(ErrorCode.Validation, "Unsupported signature format", {
        safeMessage: "A assinatura precisa ser uma imagem PNG ou JPEG. Outros formatos (SVG, WEBP) não são aceitos porque não podem ser impressos no documento assinado."
      });
    }
    // ~1,37 byte por caractere de base64; o limite protege o armazenamento
    // do navegador, onde o cadastro vive hoje, de estourar com um scan em
    // alta resolução e derrubar gravações seguintes sem aviso.
    const approximateBytes = Math.floor((value.length - value.indexOf(",") - 1) * 0.75);
    if (approximateBytes > 2_000_000) {
      throw new AppError(ErrorCode.Validation, "Signature too large", {
        safeMessage: "A imagem da assinatura passa de 2 MB. Envie uma versão menor — uma assinatura recortada costuma ter poucos KB."
      });
    }

    const signatureId = await this.identityRepo.saveProfessionalSignature(id, value);

    const updated: Professional = {
      ...existing,
      signatureImageId: signatureId,
      version: (existing.version || 1) + 1,
      updatedAt: new Date().toISOString() as never
    };
    await this.identityRepo.saveProfessional(updated);

    if (this.audit) {
      // O conteúdo da imagem NUNCA entra na auditoria — só o fato e o id.
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Professional",
        entityId: id,
        previousState: { signatureImageId: existing.signatureImageId ?? null },
        newState: { signatureImageId: signatureId },
        metadata: { action: "PROFESSIONAL_SIGNATURE_SAVED" }
      }).catch(() => {});
    }

    return updated;
  }

  public async removeProfessionalSignature(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    const existing = await this.identityRepo.getProfessional(id as ProfessionalId);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Profissional não encontrado.");
    }

    await this.identityRepo.deleteProfessionalSignature(id);
    
    const updated: Professional = {
      ...existing,
      signatureImageId: null,
      version: (existing.version || 1) + 1,
      updatedAt: new Date().toISOString() as any
    };
    await this.identityRepo.saveProfessional(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Professional",
        entityId: id,
        previousState: { signatureImageId: existing.signatureImageId ?? null },
        newState: { signatureImageId: null },
        metadata: { action: "PROFESSIONAL_SIGNATURE_REMOVED" }
      }).catch(() => {});
    }
  }

  // ---------------------------------------------------------------------------
  // ACCESS CONTROL & ROLES
  // ---------------------------------------------------------------------------
  public async listUserAccesses(context: RequestContext, userId?: string): Promise<UserClientAccess[]> {
    this.auth.require(context, PermissionCode.IdentityRead);
    return this.identityRepo.listAccesses(userId as UserId, context.activeClientId);
  }

  public async grantUserAccess(
    context: RequestContext,
    data: { userId: string; clientId: string; roleIds: string[] }
  ): Promise<UserClientAccess> {
    this.auth.require(context, PermissionCode.IdentityManage);

    const now = new Date().toISOString();
    const id = `acc-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
    const access: UserClientAccess = {
      id: id as UserClientAccessId,
      userId: data.userId as UserId,
      clientId: data.clientId as ClientId,
      roleIds: data.roleIds as RoleId[],
      status: UserClientAccessStatus.Active,
      version: 1,
      createdAt: now as any,
      updatedAt: now as any,
      deletedAt: null,
      deletedBy: null,
      deletionReason: null
    };

    await this.identityRepo.saveAccess(access);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "UserClientAccess",
        entityId: access.id,
        previousState: null,
        newState: { userId: access.userId, clientId: access.clientId, roleIds: access.roleIds },
        metadata: { action: "CLIENT_ACCESS_GRANTED" }
      }).catch(() => {});
    }

    return access;
  }

  public async revokeUserAccess(context: RequestContext, accessId: string): Promise<void> {
    this.auth.require(context, PermissionCode.IdentityManage);
    await this.identityRepo.revokeAccess(accessId as UserClientAccessId);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "UserClientAccess",
        entityId: accessId,
        previousState: null,
        newState: { status: "REVOKED" },
        metadata: { action: "CLIENT_ACCESS_REVOKED" }
      }).catch(() => {});
    }
  }

  public async transferSubWorkOrder(
    context: RequestContext,
    data: { subWorkOrderId: string; newProfessionalId: string; reason: string }
  ): Promise<SubWorkOrder> {
    this.requireAny(context, [PermissionCode.SubWorkOrderUpdate, PermissionCode.AdministrationManage]);
    
    if (!data.reason || data.reason.trim().length < 5) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória para transferência.");
    }

    const swos = await this.adminRepo.getSubWorkOrders(context.activeClientId);
    const swo = swos.find(s => s.id === data.subWorkOrderId);
    if (!swo) throw new AppError(ErrorCode.NotFound, "Sub-OS não encontrada.");
    
    const professional = await this.identityRepo.getProfessional(data.newProfessionalId as ProfessionalId);
    if (!professional) throw new AppError(ErrorCode.NotFound, "Profissional não encontrado.");

    const oldProfessionalId = swo.professionalId;
    const updated: SubWorkOrder = {
      ...swo,
      professionalId: data.newProfessionalId as ProfessionalId,
      updatedAt: new Date().toISOString()
    };
    
    await this.adminRepo.saveSubWorkOrder(updated);
    
    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "SubWorkOrder",
        entityId: swo.id,
        previousState: { professionalId: oldProfessionalId },
        newState: { professionalId: data.newProfessionalId },
        metadata: {
          action: "SUBOS_TRANSFERRED_TO_PROFESSIONAL",
          oldProfessionalId,
          newProfessionalId: data.newProfessionalId,
          reason: data.reason
        }
      }).catch(() => {});
    }
    
    return updated;
  }


  public async getAuthorizedSubWorkOrdersForProfessional(
    context: RequestContext,
    professionalId: string,
    clientId?: string,
    workOrderId?: string
  ): Promise<SubWorkOrder[]> {
    const targetClientId = (clientId || context.activeClientId) as ClientId;

    const allSubWorkOrders = await this.adminRepo.getSubWorkOrders(
      targetClientId,
      workOrderId as WorkOrderId
    );

    return allSubWorkOrders.filter(s => s.professionalId === professionalId && s.status !== 'CANCELLED');
  }

  public async validateProfessionalSubOsAuthorization(
    context: RequestContext,
    professionalId: string,
    clientId: string,
    workOrderId: string,
    subWorkOrderId: string
  ): Promise<SubWorkOrder> {
    const targetClientId = (clientId || context.activeClientId) as ClientId;

    // 1. Verify Professional exists
    const professional = await this.identityRepo.getProfessional(professionalId as ProfessionalId);
    if (!professional) {
      throw new AppError(ErrorCode.NotFound, `Profissional '${professionalId}' não encontrado.`);
    }

    // 2. Verify WorkOrder exists for client
    const workOrders = await this.adminRepo.getWorkOrders(targetClientId);
    const workOrder = workOrders.find(wo => wo.id === workOrderId);
    if (!workOrder) {
      throw new AppError(ErrorCode.NotFound, `Ordem de Serviço (OS) '${workOrderId}' não encontrada para o cliente.`);
    }

    // 3. Verify Sub-OS exists for WorkOrder and client
    const subWorkOrders = await this.adminRepo.getSubWorkOrders(targetClientId, workOrderId as WorkOrderId);
    const subWorkOrder = subWorkOrders.find(s => s.id === subWorkOrderId);
    if (!subWorkOrder) {
      throw new AppError(ErrorCode.NotFound, `Sub-OS '${subWorkOrderId}' não encontrada ou não pertence à OS selecionada.`);
    }

    // 4. Verify Professional is assigned
    if (subWorkOrder.professionalId !== professionalId) {
      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.StatusChange,
          entityType: "SubWorkOrder",
          entityId: subWorkOrderId,
          previousState: null,
          newState: null,
          metadata: {
            action: "UNAUTHORIZED_SUBOS_ATTEMPT",
            professionalId,
            clientId: targetClientId,
            workOrderId,
            subWorkOrderId
          }
        }).catch(() => {});
      }
      throw new AppError(ErrorCode.Authorization, `Acesso negado: Profissional '${professionalId}' não possui vínculo ativo com a Sub-OS '${subWorkOrderId}'.`);
    }

    return subWorkOrder;
  }

  public async listProfessionalSubOsAccesses(context: RequestContext): Promise<EnrichedProfessionalSubOsAccess[]> {
    this.requireAny(context, [PermissionCode.ProfessionalSubOsRead, PermissionCode.AdministrationRead, PermissionCode.AdministrationManage]);
    if (this.adminRepo.getProfessionalSubOsAccesses) {
      const accesses = await this.adminRepo.getProfessionalSubOsAccesses(context.activeClientId);
      return accesses as EnrichedProfessionalSubOsAccess[];
    }
    return [];
  }
  /**
   * Colaboradores de uma Sub-OS: quem, além do responsável, pode apontar RDO
   * nela. O vínculo é uma entidade própria — o responsável da Sub-OS continua
   * sendo o campo `professionalId` e não precisa de acesso duplicado.
   */
  public async listSubOsCollaborators(context: RequestContext, subWorkOrderId: string): Promise<ProfessionalSubOsAccess[]> {
    this.requireAny(context, [PermissionCode.ProfessionalSubOsRead, PermissionCode.AdministrationRead, PermissionCode.AdministrationManage]);
    if (!this.adminRepo.getProfessionalSubOsAccesses) return [];
    const accesses = await this.adminRepo.getProfessionalSubOsAccesses(context.activeClientId);
    return accesses.filter(a => a.subWorkOrderId === subWorkOrderId);
  }

  public async grantSubOsAccess(context: RequestContext, subWorkOrderId: string, professionalId: string): Promise<void> {
    this.requireAny(context, [PermissionCode.ProfessionalSubOsAssign, PermissionCode.AdministrationManage]);
    if (!this.adminRepo.saveProfessionalSubOsAccess || !this.adminRepo.getProfessionalSubOsAccesses) {
      throw new AppError(ErrorCode.NotFound, "Professional sub work order accesses not supported", {
        safeMessage: "Vínculo de colaborador à Sub-OS indisponível nesta sessão."
      });
    }

    const subOrders = await this.adminRepo.getSubWorkOrders();
    const sub = subOrders.find(s => s.id === subWorkOrderId);
    if (!sub) {
      throw new AppError(ErrorCode.NotFound, `Sub work order ${subWorkOrderId} not found`, {
        safeMessage: "Sub-OS não encontrada."
      });
    }

    const professional = await this.identityRepo.getProfessional(professionalId as ProfessionalId);
    if (!professional || professional.deletedAt || professional.status !== ProfessionalStatus.Active) {
      throw new AppError(ErrorCode.Validation, "Professional inactive or not found", {
        safeMessage: "Profissional inválido ou inativo."
      });
    }

    const existing = (await this.adminRepo.getProfessionalSubOsAccesses(sub.clientId))
      .find(a => a.subWorkOrderId === subWorkOrderId && a.professionalId === professionalId);

    const now = new Date().toISOString();
    // Reativar um vínculo revogado preserva o registro em vez de criar outro.
    await this.adminRepo.saveProfessionalSubOsAccess({
      id: existing?.id ?? `psoa-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
      clientId: sub.clientId,
      professionalId: professionalId as ProfessionalId,
      subWorkOrderId,
      status: "ACTIVE",
      createdAt: existing?.createdAt ?? now,
      updatedAt: now
    });
  }

  public async revokeSubOsAccess(context: RequestContext, accessId: string): Promise<void> {
    this.requireAny(context, [PermissionCode.ProfessionalSubOsRemove, PermissionCode.ProfessionalSubOsInactivate, PermissionCode.AdministrationManage]);
    if (!this.adminRepo.deleteProfessionalSubOsAccess) return;
    await this.adminRepo.deleteProfessionalSubOsAccess(accessId);
  }

  public async requestProtectedDeletion(
    context: RequestContext,
    entityType: "USER" | "PROFESSIONAL" | "SIGNATORY",
    entityId: string,
    passwordAttempt: string,
    reason: string
  ): Promise<{ success: boolean; resultType: "HARD_DELETE" | "SOFT_DELETE" | "ERROR"; message?: string }> {
    this.auth.require(context, PermissionCode.AdministrationManage);

    const currentUserId = context.userId;
    if (!currentUserId) {
      throw new AppError(ErrorCode.Authorization, "Usuário não autenticado.");
    }

    const currentUser = await this.identityRepo.getUser(currentUserId as any);
    if (!currentUser) {
      throw new AppError(ErrorCode.NotFound, "Usuário autenticado não encontrado.");
    }

    // Validação de senha
    if (!(await passwordMatches(currentUser, passwordAttempt))) {
      if (this.audit) {
        await this.audit.record(context, {
          action: "DELETE_AUTH_FAILED" as any,
          entityType,
          entityId,
          previousState: null,
          newState: null,
          metadata: { reason: "Senha incorreta na exclusão protegida" }
        }).catch(() => {});
      }
      return { success: false, resultType: "ERROR", message: "Senha incorreta. Exclusão não realizada." };
    }

    // Regras por entidade
    if (entityType === "USER") {
      this.auth.require(context, PermissionCode.UserDelete);

      if (entityId === currentUserId) {
        return { success: false, resultType: "ERROR", message: "Não é possível excluir o usuário atualmente logado." };
      }

      const userTarget = await this.identityRepo.getUser(entityId as any);
      if (!userTarget) return { success: false, resultType: "ERROR", message: "Usuário não encontrado." };

      // Last-active-Administrator protection (Bloco 8): a real dynamic count
      // of active users with the ADMIN role, never a hardcoded seed user id
      // — the previous check (`userTarget.id === "usr_admin"`) only ever
      // protected one specific seeded account and let every other admin be
      // deleted down to zero, which defeats the purpose of the rule.
      if ((userTarget.profileType || "").toUpperCase() === RoleCode.Admin) {
        const allUsers = await this.identityRepo.listUsers();
        const otherActiveAdmins = allUsers.filter(
          u => u.id !== entityId && u.status === UserStatus.Active && (u.profileType || "").toUpperCase() === RoleCode.Admin
        );
        if (otherActiveAdmins.length === 0) {
          return { success: false, resultType: "ERROR", message: "Não é possível excluir o último administrador ativo." };
        }
      }

      // Real history check (Bloco 8) — a User must be preserved (soft
      // delete / DESATIVAR only) when it is linked to a Professional record,
      // or when it authored an Engineering Minuta analysis/validation/
      // approval (analysisStartedBy/validatedByUserId/approvedBy in
      // src/domain/report/minuta-domain.ts — all store userId). A user with
      // neither is safe to hard-delete (e.g. a freshly created test user).
      const profs = await this.identityRepo.listProfessionals();
      const hasProfessionalLink = profs.some(p => p.userId === entityId);
      const hasMinutaHistory = this.userHistoryChecker ? await this.userHistoryChecker(entityId) : false;
      const hasHistory = hasProfessionalLink || hasMinutaHistory;

      if (hasHistory) {
        await this.identityRepo.inactivateUser(entityId as any);

        if (this.audit) {
          await this.audit.record(context, {
            action: AuditAction.Inactivate,
            entityType: "User",
            entityId,
            previousState: { status: userTarget.status },
            newState: { status: "INACTIVE" },
            metadata: { action: "USER_DEACTIVATED", reason, hasProfessionalLink, hasMinutaHistory }
          }).catch(() => {});
        }

        return {
          success: true,
          resultType: "SOFT_DELETE",
          message: "Este usuário possui histórico preservado (vínculo profissional ou autoria de análise/validação/aprovação de Minuta) — foi apenas desativado, não é possível excluí-lo fisicamente."
        };
      }

      await this.identityRepo.deleteUser(entityId as any);

      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.Delete,
          entityType: "User",
          entityId,
          previousState: null,
          newState: null,
          metadata: { action: "USER_DELETED", reason }
        }).catch(() => {});
      }

      return { success: true, resultType: "HARD_DELETE" };

    } else if (entityType === "PROFESSIONAL") {
      const profTarget = await this.identityRepo.getProfessional(entityId as any);
      if (!profTarget) return { success: false, resultType: "ERROR", message: "Profissional não encontrado." };

      const isEngineer = profTarget.professionalType === "ENGINEER" || profTarget.functionTitle?.toLowerCase().includes("plh") || profTarget.functionTitle?.toLowerCase().includes("engenheiro habilitado");

      if (isEngineer && this.projectChecker && (this.projectChecker as any).listProjects) {
        const allProjects = await (this.projectChecker as any).listProjects();
        const conflictingProjects = allProjects.filter((p: any) => p.engineerProfessionalId === entityId);
        if (conflictingProjects.length > 0) {
          return { success: false, resultType: "ERROR", message: "Defina outro Engenheiro/PLH responsável antes de excluir ou inativar este profissional." };
        }
      }

      if ((this.identityRepo as any).deleteProfessional) {
        await (this.identityRepo as any).deleteProfessional(entityId as any);
      }
      if (this.audit) {
        await this.audit.record(context, { action: AuditAction.Delete, entityType: "Professional", entityId, previousState: null, newState: null, metadata: { action: "PROFESSIONAL_DELETED", reason } }).catch(() => {});
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { type: 'PROFESSIONAL', id: entityId } }));
      }
      return { success: true, resultType: "HARD_DELETE" };

    } else if (entityType === "SIGNATORY") {
      // O signatário é localizado pelo PRÓPRIO id. Procurá-lo dentro de
      // `getSignatories(context.activeClientId)` fazia a exclusão falhar com
      // "Signatário não encontrado" sempre que a tela estivesse noutro
      // Cliente — ou em nenhum —, embora o registro existisse.
      const signatory = await this.findSignatoryById(entityId);
      if (!signatory) return { success: false, resultType: "ERROR", message: "Signatário não encontrado." };

      const createdAt = new Date(signatory.createdAt);
      const hasHistory = (new Date().getTime() - createdAt.getTime()) > 60000;

      if (hasHistory) {
        // Inativa pelo Cliente DO REGISTRO, não pelo da sessão.
        await this.adminRepo.inactivateSignatory(signatory.clientId, entityId as any);
        if (this.audit) {
          await this.audit.record(context, { action: AuditAction.Inactivate, entityType: "Signatory", entityId, previousState: { status: signatory.status }, newState: { status: "INACTIVE" }, metadata: { action: "SIGNATORY_DEACTIVATED", reason } }).catch(() => {});
        }
        return { success: true, resultType: "SOFT_DELETE" };
      } else {
        await this.adminRepo.deleteSignatory(entityId as any);
        if (this.audit) {
          await this.audit.record(context, { action: AuditAction.Delete, entityType: "Signatory", entityId, previousState: null, newState: null, metadata: { action: "SIGNATORY_DELETED", reason } }).catch(() => {});
        }
        return { success: true, resultType: "HARD_DELETE" };
      }
    }

    return { success: false, resultType: "ERROR", message: "Tipo de entidade desconhecido." };
  }

}

